import CryptoJS from "crypto-js";
import ReactPixel from "react-facebook-pixel";
import {
  formatPhoneNumber,
  formatPhoneNumberIntl,
} from "react-phone-number-input";
import { toast } from "react-toastify";

import CustomLinkWrapper from "../common/CustomLinkWrapper";
import { CAR_SKIP_OWNER_PIN } from "../for-service-provider/carInEscrowSignup";
import {
  ALLOWED_PREFIXES,
  CountyVertical,
  CUSTOMER_HOME_PATH,
  getCarUrl,
  getCustomerStagingUrl,
  getDefaultUrl,
  getServiceProvidersUrl,
  isCarHost,
  isLocalDevHost,
  isServiceProviderPortalHost,
  isServiceProviderPortalRole,
  iv,
  MONTHS,
  PROFESSIONAL_TYPE,
  ROLES,
  secretKey,
  SERVER,
  SUBDOMAIN,
  userTimeZone,
  USA_STATES,
} from "./constants";

export { isLocalDevHost };

/** Same-origin relative path only; blocks cross-domain previousPath values. */
export const resolveInAppPath = (path) => {
  if (!path || typeof path !== "string") {
    return null;
  }

  const trimmed = path.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const url = new URL(trimmed);
      if (
        typeof window === "undefined" ||
        url.origin !== window.location.origin
      ) {
        return null;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  return trimmed.startsWith("/") ? trimmed : null;
};

export const resolveAppUrl = (path = "") => {
  const normalizedPath = String(path || "").replace(/^\//, "");
  return `${getDefaultUrl()}${normalizedPath}`;
};

export const redirectToAppPath = (path = "") => {
  window.location.href = resolveAppUrl(path);
};

// to check url is image type or not
export const isImage = (url) => {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
};

// check any object contains particular key or not
const checkIfKeyExist = (objectName, keyName) => {
  let keyExist = Object.keys(objectName).some((key) => key === keyName);
  return keyExist;
};

export const generateTitleChainList = (propertiesDetails) => {
  const titleChainList = [];

  if (checkIfKeyExist(propertiesDetails, "GeneralInfo")) {
    titleChainList.push({
      type: "Year Built",
      year: propertiesDetails?.GeneralInfo[0]?.["Sale Date"]?.split("/")[2],
      sale_price: propertiesDetails?.GeneralInfo[0]?.["Year Built"],
      doc_link: propertiesDetails?.GeneralInfo[0]?.Doc_link,
      // doc_link: "https://www.google.com",
      deed_type: propertiesDetails?.GeneralInfo[0]?.["Property Type"],
    });
  }
  if (checkIfKeyExist(propertiesDetails, "SalesHistory")) {
    titleChainList.push({
      type: "Sale Deed",
      year: propertiesDetails?.SalesHistory[0]?.["Recording Date"]?.split(
        "/",
      )[2],
      sale_price: propertiesDetails?.SalesHistory[0]?.["Sale Price"],
      doc_link: propertiesDetails?.SalesHistory[0]?.Doc_link,
      deed_type: propertiesDetails?.SalesHistory[0]?.["Deed Type"],
    });
  }
  if (checkIfKeyExist(propertiesDetails, "FinanceHistory")) {
    titleChainList.push({
      type: "Mortgage",
      year: propertiesDetails?.FinanceHistory[0]?.["Loan Date"].split("/")[2],
      loan_amount: propertiesDetails?.FinanceHistory[0]?.["Loan Amount"],
      doc_link: propertiesDetails?.FinanceHistory[0]?.["Doc_link"],
      loan_type: propertiesDetails?.FinanceHistory[0]?.["Loan Type"],
    });
  }
  if (checkIfKeyExist(propertiesDetails, "ForeclosureInfo")) {
    titleChainList.push({
      type: "Mortgage",
      year: propertiesDetails?.ForeclosureInfo[0]?.["Loan Date"].split("/")[2],
      loan_amount: propertiesDetails?.ForeclosureInfo[0]?.["Loan Amount"],
      doc_link: propertiesDetails?.ForeclosureInfo[0]?.["Doc_link"],
      loan_type: propertiesDetails?.ForeclosureInfo[0]?.["Loan Type"],
    });
  }
  return titleChainList;
};

// to split the name from the url after specific index
export const extractNameFromUrl = (url, seperator, targetIndex) => {
  return url.split(seperator)[targetIndex];
};

// check url is valid or not
export const urlPatternValidation = (URL) => {
  const regex = new RegExp(
    "(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?",
  );
  return regex.test(URL);
};

// it will return formated amount if amount is too big like 1.2K, 15.5M
export const formatAmount = (number, decPlaces) => {
  decPlaces = Math.pow(10, decPlaces);

  var abbrev = ["k", "m", "b", "t"];

  for (var i = abbrev.length - 1; i >= 0; i--) {
    var size = Math.pow(10, (i + 1) * 3);

    if (size <= number) {
      number = Math.round((number * decPlaces) / size) / decPlaces;

      if (number === 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }

      number += abbrev[i];
      break;
    }
  }

  return number;
};

// generate coordinates for sale area map
export const generateSaleAreaMapCoordinates = (saleData = []) => {
  return saleData?.length > 0
    ? saleData?.map((data) => {
        return {
          address: `$ ${formatAmount(
            !isNaN(data?.sa_val_transfer) ? data?.sa_val_transfer : 0,
            2,
          )}`,
          lat: parseFloat(data?.sa_y_coord),
          lng: parseFloat(data?.sa_x_coord),
        };
      })
    : [];
};

// function for converting amount to particular currency
export const currencyFormatter = (amount, value = "0") => {
  return amount
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount)
    : value === "0"
      ? "$0.00"
      : value;
};

// round of any digit to 2 points
export const roundOfNum = (number) => {
  return (
    Math.round((parseFloat(number) + Number.EPSILON) * 100) / 100
  ).toFixed(2);
};

export const roundOfNumIntrFloat = (number) => {
  let value = number;
  if (Number.isInteger(value)) {
    value.toString(); // Return as is
  } else {
    value.toFixed(2); // Convert to fixed decimal with 2 digits after the decimal point
  }
  return value;
};

// calculate % of the area covered by particular part of chart
export const calcPercentageOfChart = (arr, eleValue) => {
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += parseFloat(arr[i]);
  }
  return roundOfNum((parseFloat(eleValue) / sum) * 100);
};

// amorization and interest calculator
export const amortizeLoan = (
  amount = 0,
  month_interest = 0,
  down_percent = 0,
  already_paid = 0,
  term = 0,
) => {
  let mortgage = amount - already_paid;
  let sale_price = parseFloat(mortgage);
  let annual_interest_percent = parseFloat(month_interest);
  let year_term = term;
  let this_year_interest_paid = 0;
  let this_year_principal_paid = 0;
  let monthly_payment = false;

  let month_term = year_term * 12;
  let down_payment = sale_price * (down_percent / 100);
  let annual_interest_rate = annual_interest_percent / 100;
  let monthly_interest_rate = annual_interest_rate / 12;
  let financing_price = sale_price - down_payment;
  let monthly_factor = get_interest_factor(year_term, monthly_interest_rate);
  monthly_payment = financing_price / monthly_factor;

  // Set some base variables
  let principal = financing_price;
  // let current_month = 0;
  let current_year = new Date().getFullYear();
  // This basically, re-figures out the monthly payment, again.
  let power = -month_term;
  let denom = Math.pow(1 + monthly_interest_rate, power);
  monthly_payment = principal * (monthly_interest_rate / (1 - denom));

  let loanData = [];
  let interest_paid, principal_paid, remaining_balance, EMIPerMonth;

  let totalInterestPaid = 0;
  let xAxis = [];
  let mobileXAxis = [];
  let chartInterest = [];
  let chartPrinciple = [];
  let chartRemaining = [];

  for (let year = current_year; year < current_year + term; year++) {
    let yearData = [];
    yearData[year] = [];
    for (let month = 0; month < 12; month++) {
      interest_paid = principal * monthly_interest_rate;
      principal_paid = monthly_payment - interest_paid;
      remaining_balance = principal - principal_paid;

      this_year_interest_paid = this_year_interest_paid + interest_paid;
      this_year_principal_paid = this_year_principal_paid + principal_paid;

      let total_spent_this_year =
        this_year_interest_paid + this_year_principal_paid;

      yearData[year].push({
        [month]: {
          interest: roundOfNum(interest_paid),
          principal: roundOfNum(principal_paid),
          remaining: roundOfNum(remaining_balance),
        },
        total: {
          interest: roundOfNum(this_year_interest_paid),
          principal: roundOfNum(this_year_principal_paid),
          sum: roundOfNum(total_spent_this_year),
        },
      });

      xAxis.push(getMonthYear(month, year));
      mobileXAxis.push(getMobileMonthYear(month, year));
      chartInterest.push(roundOfNum(interest_paid));
      chartPrinciple.push(roundOfNum(principal_paid));
      chartRemaining.push(roundOfNum(remaining_balance));

      totalInterestPaid += roundOfNum(interest_paid);

      EMIPerMonth = roundOfNum(total_spent_this_year);

      this_year_interest_paid = 0;
      this_year_principal_paid = 0;

      principal = remaining_balance;
    }
    loanData.push(yearData);
  }

  let estimatedPrice = parseFloat(mortgage) + parseFloat(totalInterestPaid);
  let estimatedProgress =
    (parseFloat(amount) /
      (parseFloat(amount) + parseFloat(totalInterestPaid))) *
    100;

  return {
    loanData,
    estimatedPrice: roundOfNum(estimatedPrice),
    EMI: roundOfNum(EMIPerMonth),
    estimatedProgress: roundOfNum(estimatedProgress),
    chartData: {
      xAxis: xAxis,
      mobileXAxis: mobileXAxis,
      chartInterest: chartInterest,
      chartPrinciple: chartPrinciple,
      chartRemaining: chartRemaining,
    },
  };
};

function get_interest_factor(year_term, monthly_interest_rate) {
  let base_rate;

  let factor = 0;
  base_rate = 1 + monthly_interest_rate;
  let denominator = base_rate;
  for (let i = 0; i < year_term * 12; i++) {
    factor += 1 / denominator;
    denominator *= base_rate;
  }
  return factor;
}

// get Month-YYYY
export const getMonthYear = (month, year) => {
  return `${MONTHS[month]} - ${year}`;
};

// get Mon-YYYY
export const getMobileMonthYear = (month, year) => {
  return `${MONTHS[month]?.slice(0, 3)} - ${String(year)?.slice(2, 4)}`;
};

// to create local preview url of uploaded file
export const createFileUrl = (file) => {
  return URL.createObjectURL(file);
};

// get date in MM-DD-YYYY format
export const getDateInMMDDYYYYFormat = (date, seperator = "-") => {
  // return `${
  //   date?.getMonth() + 1
  // }${seperator}${date?.getDate()}${seperator}${date?.getFullYear()}`;
  return [
    `0${date?.getMonth() + 1}`?.slice(-2),
    `0${date?.getDate()}`?.slice(-2),
    `${date?.getFullYear()}`,
  ]?.join(seperator);
};

// get date in YYYY-MM-DD format
export const getDateInYYYYMMDDFormat = (date, seperator = "-") => {
  return [
    `${date?.getFullYear()}`,
    `0${date?.getMonth() + 1}`?.slice(-2),
    `0${date?.getDate()}`?.slice(-2),
  ]?.join(seperator);
};

export const getPropertyRecordingInformation = (propertyData = {}) =>
  propertyData?.recording_info ||
  propertyData?.booking_page ||
  propertyData?.instrument_number ||
  "";

const UCC_MONTH_NAME_TO_NUMBER = {
  january: "01",
  february: "02",
  march: "03",
  april: "04",
  may: "05",
  june: "06",
  july: "07",
  august: "08",
  september: "09",
  october: "10",
  november: "11",
  december: "12",
};

export const formatIsoDateToMMDDYYYY = (isoDate, separator = "/") => {
  if (!isoDate) return "";
  const [year, month, day] = String(isoDate).split("-");
  if (!year || !month || !day) return "";
  return [month, day, year].join(separator);
};

export const getPropertyRecordingDateMMDDYYYY = (
  propertyData = {},
  separator = "/",
) => {
  const rawDate = propertyData?.recording_date || propertyData?.recordingDate;
  if (!rawDate) return "";

  const normalized = normalizeUccRecordingDateForInput(rawDate);
  if (normalized) {
    return formatIsoDateToMMDDYYYY(normalized, separator);
  }

  return String(rawDate);
};

export const formatDisplayDateMMDDYYYY = (value, separator = "-") => {
  if (!value) return "-";

  const normalized = normalizeUccRecordingDateForInput(value);
  if (normalized) {
    return formatIsoDateToMMDDYYYY(normalized, separator);
  }

  return String(value);
};

const addressTailTokens = (street) =>
  String(street || "")
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .slice(1); // the street itself never counts as a city

const tokenHasPart = (token, part) =>
  token === part || token.startsWith(`${part} `) || token.endsWith(` ${part}`);

/**
 * One display line for a property address. The stored street often already
 * ends with the city ("8653 N Beach St, Fort Worth"), so a city / state / zip
 * part the street already contains is left out instead of repeated.
 * Same rule as the backend propertyAddressLine().
 */
export const propertyAddressLine = (street, ...parts) => {
  const base = String(street || "").trim();
  const tokens = addressTailTokens(base);
  const extra = parts
    .map((part) => String(part ?? "").trim())
    .filter(
      (part) =>
        part && !tokens.some((token) => tokenHasPart(token, part.toLowerCase())),
    );
  return [base, ...extra].filter(Boolean).join(", ");
};

/**
 * Street only, for two-line layouts (street / city, state zip): drops a
 * trailing city / state / zip the stored street already carries.
 */
export const propertyStreetLine = (street, ...parts) => {
  const pieces = String(street || "")
    .split(",")
    .map((piece) => piece.trim());
  const tail = [...parts, "usa", "united states"]
    .map((part) => String(part ?? "").trim().toLowerCase())
    .filter(Boolean);
  while (pieces.length > 1) {
    const last = pieces[pieces.length - 1].toLowerCase();
    if (last && !tail.some((part) => tokenHasPart(last, part))) {
      break;
    }
    pieces.pop();
  }
  return pieces.filter(Boolean).join(", ");
};

/**
 * Split property address for My Properties header:
 * line 1 = site street or parcel, line 2 = city/state/zip, line 3 = county label.
 */
export const formatPropertyAddressLines = (property = {}) => {
  const addressFirst = property?.address?.split(",")?.[0]?.trim();
  const siteLine =
    addressFirst?.length > 0
      ? addressFirst
      : property?.parcel_number
        ? `Raw Parcel: ${property.parcel_number}`
        : property?.address?.trim() || "-";

  const city = property?.city?.trim();
  const state = property?.state?.trim();
  const zip = property?.zip?.trim();
  const cityStateParts = [city, state].filter(Boolean);
  let cityStateZip = cityStateParts.join(", ");
  if (zip) {
    cityStateZip = cityStateZip ? `${cityStateZip} ${zip}` : zip;
  }

  if (!cityStateZip && property?.address) {
    const parts = String(property.address)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length > 1) {
      cityStateZip = parts.slice(1).join(", ");
    }
  }

  let jsonResponse = property?.json_response ?? property?.json_res ?? null;
  if (typeof jsonResponse === "string") {
    try {
      jsonResponse = JSON.parse(jsonResponse);
    } catch {
      jsonResponse = null;
    }
  }

  const propertyDetails =
    typeof property?.property_details === "object"
      ? property.property_details
      : null;

  const countyRaw =
    property?.county ||
    property?.county_name ||
    jsonResponse?.mm_muni_name ||
    propertyDetails?.mm_muni_name ||
    propertyDetails?.LocationInfo?.County ||
    propertyDetails?.locationInfo?.County ||
    null;
  const county =
    typeof countyRaw === "string" ? countyRaw.trim() : countyRaw || null;

  return {
    siteLine: withPropertyUnit(siteLine, property),
    cityStateZip: cityStateZip || null,
    county: county || null,
  };
};

/** Unit # for a property row: the stored unit, else the one in its search/TTB data. */
export const getPropertyUnitNo = (property = {}) => {
  let jsonResponse = property?.json_response ?? property?.json_res ?? null;
  if (typeof jsonResponse === "string") {
    try {
      jsonResponse = JSON.parse(jsonResponse);
    } catch {
      jsonResponse = null;
    }
  }
  const propertyDetails =
    typeof property?.property_details === "object"
      ? property.property_details
      : null;

  return (
    property?.unit_no ||
    jsonResponse?.sa_site_unit_val ||
    propertyDetails?.sa_site_unit_val ||
    ""
  );
};

/** Street line with the property's unit: "240 E 38th St" → "240 E 38th St #5A". */
export const withPropertyUnit = (siteLine, property) =>
  appendUnitToSiteLine(siteLine, getPropertyUnitNo(property));

const UNIT_MARKER_PATTERN = /#|\b(unit|apt|apartment|ste|suite)\b/i;

/** "240 E 38th St" + "5A" → "240 E 38th St #5A", unless the line already names a unit. */
const appendUnitToSiteLine = (siteLine, unitNo) => {
  const unit = String(unitNo ?? "")
    .trim()
    .replace(/^#\s*/, "");
  if (
    !unit ||
    unit === "0" ||
    !siteLine ||
    siteLine === "-" ||
    siteLine.startsWith("Raw Parcel") ||
    UNIT_MARKER_PATTERN.test(siteLine) ||
    siteLine.toLowerCase().endsWith(` ${unit.toLowerCase()}`)
  ) {
    return siteLine;
  }
  return `${siteLine} #${unit}`;
};

export const getPropertyRecordingDateYYYYMMDD = (
  propertyData = {},
  separator = "-",
) => {
  const rawDate = propertyData?.recording_date || propertyData?.recordingDate;
  if (!rawDate) return "";

  const normalized = normalizeUccRecordingDateForInput(rawDate);
  if (normalized) {
    return normalized.split("-").join(separator);
  }

  return String(rawDate);
};

export const isEpAdminUser = (userProfileDetails = {}) =>
  userProfileDetails?.data?.role?.includes(ROLES?.admin) ||
  userProfileDetails?.data?.role?.includes(ROLES?.subAdmin);

export const userHasSecurityPin = (userOrProfile = {}) => {
  if (!userOrProfile || typeof userOrProfile !== "object") {
    return false;
  }
  if (userOrProfile.security_pin_added === true) {
    return true;
  }
  const pin = userOrProfile.security_pin;
  return pin != null && String(pin).length > 0;
};

// The security-PIN step applies only to Complete Protection. Basic and enhanced
// monitoring categories skip the PIN and go straight to monitoring completion.
export const requiresSecurityPin = (category) =>
  category === "complete_protection";

const TERMINATION_STREET_SUFFIX_PATTERN =
  /\b([A-Za-z0-9]+)(Rd|Rd\.|St|St\.|Ave|Ave\.|Dr|Dr\.|Ln|Ln\.|Blvd|Blvd\.|Ct|Ct\.|Way|Cir)\b/gi;

export const toTitleCaseWords = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());

export const normalizeStreetSuffixSpacing = (value = "") =>
  String(value).replace(
    TERMINATION_STREET_SUFFIX_PATTERN,
    (_, prefix, suffix) => `${prefix} ${suffix}`,
  );

export const sanitizeTerminationStreetAddress = (value = "") => {
  const cleaned = normalizeStreetSuffixSpacing(
    String(value).trim().replace(/\s+/g, " "),
  );
  return cleaned ? toTitleCaseWords(cleaned) : "";
};

export const sanitizeTerminationCity = (value = "") =>
  toTitleCaseWords(String(value).trim());

export const sanitizeTerminationState = (value = "") => {
  const state = String(value).trim();
  if (!state) return "";
  return state.length === 2 ? state.toUpperCase() : toTitleCaseWords(state);
};

export const sanitizeTerminationZip = (value = "") =>
  String(value).replace(/\D/g, "").slice(0, 5);

export const getTerminationStreetAddress = (propertyData = {}) => {
  const raw = propertyData?.address ?? "";
  const street = raw.includes(",") ? raw.split(",")[0] : raw;
  return sanitizeTerminationStreetAddress(street);
};

export const resolvePropertyOwnerDisplayName = (propertyData = {}) => {
  const directName = String(
    propertyData?.owner_name || propertyData?.ownerName || "",
  ).trim();
  if (directName) {
    return directName;
  }

  const ownerInfo = propertyData?.property_details?.OwnerInfo;
  const fromOwnerInfo = String(ownerInfo?.["Owner Name"] || "").trim();
  if (fromOwnerInfo) {
    return fromOwnerInfo;
  }

  return formatTitleOwnerNamesFromJson(propertyData);
};

export const sanitizeTerminationPropertyData = (propertyData = {}) => ({
  ...propertyData,
  address: getTerminationStreetAddress(propertyData),
  city: sanitizeTerminationCity(propertyData?.city ?? ""),
  state: sanitizeTerminationState(propertyData?.state ?? ""),
  zip: sanitizeTerminationZip(propertyData?.zip ?? ""),
  owner_name: resolvePropertyOwnerDisplayName(propertyData),
});

export const mergeUccTerminationPrefill = (
  propertyData = {},
  prefillResponse = {},
) => {
  const formValues = prefillResponse?.data?.form_values || {};
  if (!Object.keys(formValues).length) {
    return sanitizeTerminationPropertyData(propertyData);
  }

  const pickValue = (nextValue, currentValue) => {
    if (nextValue === null || nextValue === undefined || nextValue === "") {
      return currentValue;
    }
    return nextValue;
  };

  return sanitizeTerminationPropertyData({
    ...propertyData,
    recording_date: pickValue(
      formValues.recording_date,
      propertyData?.recording_date,
    ),
    recordingDate: pickValue(
      formValues.recording_date,
      propertyData?.recordingDate,
    ),
    instrument_number: pickValue(
      formValues.instrument_number,
      propertyData?.instrument_number,
    ),
    booking_page: pickValue(
      formValues.booking_page,
      propertyData?.booking_page,
    ),
    recording_info: pickValue(
      formValues.recording_info,
      propertyData?.recording_info,
    ),
    description_of_property: pickValue(
      formValues.description_of_property,
      propertyData?.description_of_property,
    ),
    descriptionOfProperty: pickValue(
      formValues.description_of_property,
      propertyData?.descriptionOfProperty,
    ),
    legal_property_description: pickValue(
      formValues.legal_property_description,
      propertyData?.legal_property_description,
    ),
    legal_description_long: pickValue(
      formValues.legal_property_description,
      propertyData?.legal_description_long,
    ),
    address: pickValue(formValues.address, propertyData?.address),
    city: pickValue(formValues.city, propertyData?.city),
    state: pickValue(formValues.state, propertyData?.state),
    zip: pickValue(formValues.zip, propertyData?.zip),
    owner_name: pickValue(
      formValues.owner_name,
      resolvePropertyOwnerDisplayName(propertyData),
    ),
    ucc_prefill: prefillResponse?.data || null,
  });
};

export const getUccTerminationPrefillUrl = (
  propertyId,
  { isAdmin = false, isServiceProvider = false } = {},
) => {
  if (isAdmin) {
    return `admin/property/${propertyId}/ucc-termination-prefill`;
  }
  if (isServiceProvider) {
    return `service-provider/property/${propertyId}/ucc-termination-prefill`;
  }
  return `customer/property/${propertyId}/ucc-termination-prefill`;
};

export const fetchUccRecordingOcrPreview = async (
  propertyId,
  file,
  { isServiceProvider = false } = {},
) => {
  if (!propertyId || !file) {
    return {
      success: false,
      message:
        "Property and recorded UCC document are required for OCR preview.",
    };
  }

  const { postAPIPayload } = await import("../../apis/postApi");
  const { mapCreateUserApiUrl } =
    await import("../for-service-provider/createUserApiPaths");
  const formData = new FormData();
  formData.append("document", file);

  return postAPIPayload({
    url: mapCreateUserApiUrl(
      `admin/property/${propertyId}/ucc-recording-ocr`,
      isServiceProvider,
    ),
    data: formData,
    token: true,
    isFile: true,
  });
};

export const fetchUccTerminationPrefill = async (
  propertyData = {},
  { isAdmin = false, isServiceProvider = false } = {},
) => {
  const propertyId = propertyData?.id ?? propertyData?._id;
  if (!propertyId) {
    return {
      success: false,
      propertyData,
      message: "Property id is required for UCC prefill.",
    };
  }

  const { getAPIPayload } = await import("../../apis/getApi");
  const response = await getAPIPayload({
    url: getUccTerminationPrefillUrl(propertyId, {
      isAdmin,
      isServiceProvider,
    }),
    token: true,
  });

  if (!response?.success) {
    return {
      success: false,
      propertyData,
      message:
        response?.message ||
        response?.data?.message ||
        "Unable to load UCC document data.",
      response,
    };
  }

  return {
    success: true,
    propertyData: mergeUccTerminationPrefill(propertyData, response),
    message: response?.message || "UCC document data loaded.",
    response,
  };
};

export const openDocumentsInNewTabs = (urls = []) => {
  urls.filter(Boolean).forEach((url) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};

export const fetchExistingServicerUnlockPackage = async (propertyId) => {
  if (!propertyId) {
    return { has_existing_package: false };
  }

  const { getAPIPayload } = await import("../../apis/getApi");
  const response = await getAPIPayload({
    url: `customer/property/${propertyId}/existing-servicer-unlock-documents`,
    token: true,
  });

  if (!response?.success) {
    return {
      has_existing_package: false,
      message:
        response?.message ||
        response?.data?.message ||
        "Unable to check for existing unlock documents.",
    };
  }

  return response?.data || { has_existing_package: false };
};

export const withdrawUnlockPackage = async (
  propertyId,
  { admin = false } = {},
) => {
  if (!propertyId) {
    return { success: false, message: "Property is required." };
  }

  const { postAPIPayload } = await import("../../apis/postApi");
  const url = admin
    ? `admin/property/${propertyId}/revoke-unlock-package`
    : `customer/property/${propertyId}/revoke-unlock-package`;

  return postAPIPayload({
    url,
    data: {},
    token: true,
  });
};

export const fetchSuccessfulTerminationPrefill = async (
  propertyId,
  transactionDate,
) => {
  const { getAPIPayload } = await import("../../apis/getApi");
  const response = await getAPIPayload({
    url: `admin/property/${propertyId}/successful-termination-prefill?transaction_date=${encodeURIComponent(transactionDate)}`,
    token: true,
  });

  return response?.data ?? {};
};

export const completeSuccessfulTermination = async (propertyId, formData) => {
  const { postAPIPayload } = await import("../../apis/postApi");
  return postAPIPayload({
    url: `admin/property/${propertyId}/successful-termination`,
    data: formData,
    token: true,
    isFile: true,
  });
};

export const generateUnlockTerminationPackage = async (
  propertyId,
  formValues = {},
  { isServiceProvider = false } = {},
) => {
  const { postAPIPayload } = await import("../../apis/postApi");
  const url = isServiceProvider
    ? `service-provider/property/${propertyId}/unlock-termination-package`
    : `admin/property/${propertyId}/unlock-termination-package`;

  return postAPIPayload({
    url,
    data: {
      recording_date: formValues.recording_date || null,
      booking_page: formValues.booking_page || null,
      instrument_number: formValues.instrument_number || null,
      description_of_property: formValues.description_of_property || null,
      legal_property_description: formValues.legal_property_description || null,
    },
    token: true,
  });
};

const parseUccComparableDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slashMatch) {
    const month = slashMatch[1].padStart(2, "0");
    const day = slashMatch[2].padStart(2, "0");
    return `${slashMatch[3]}-${month}-${day}`;
  }

  const namedMonthMatch = raw.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (namedMonthMatch) {
    const month = UCC_MONTH_NAME_TO_NUMBER[namedMonthMatch[1].toLowerCase()];
    if (month) {
      const day = namedMonthMatch[2].padStart(2, "0");
      return `${namedMonthMatch[3]}-${month}-${day}`;
    }
  }

  return raw.toLowerCase();
};

export const normalizeUccRecordingDateForInput = (value) =>
  parseUccComparableDate(value) || "";

const normalizeUccComparableText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim().replace(/\s+/g, " ").toLowerCase();
};

const uccOcrValuesEquivalent = (fieldKey, ocrValue, formValue) => {
  if (fieldKey === "recording_date") {
    const ocrDate = parseUccComparableDate(ocrValue);
    const formDate = parseUccComparableDate(formValue);
    return ocrDate !== null && formDate !== null && ocrDate === formDate;
  }

  return (
    normalizeUccComparableText(ocrValue) ===
    normalizeUccComparableText(formValue)
  );
};

export const formatUccOcrDisplayValue = (fieldKey, value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (fieldKey === "recording_date") {
    const iso = parseUccComparableDate(value);
    if (iso) {
      const [year, month, day] = iso.split("-");
      return `${month}/${day}/${year}`;
    }
  }

  const text = String(value).trim();
  if (text.length > 180) {
    return `${text.slice(0, 180)}…`;
  }

  return text;
};

// Stored descriptions were entered at UCC generation and are the source of
// truth. OCR may only offer a description when nothing is stored, or when the
// stored legal description was cut off by the old 191-character column.
const uccOcrMayOfferDescription = (fieldKey, currentValue, prefillData) => {
  if (!currentValue) return true;
  return (
    fieldKey === "legal_property_description" &&
    Boolean(prefillData?.legal_description_cut_off)
  );
};

export const buildUccOcrFormReview = (
  prefillData = {},
  formValues = {},
  { formVariant = "termination" } = {},
) => {
  const extracted = prefillData?.extracted || {};
  const isRecordingDetailsForm = formVariant === "recordingDetails";

  const currentRecordingDate = isRecordingDetailsForm
    ? formValues?.recording_date
    : formValues?.recordingDate;
  const currentRecordingInfo = isRecordingDetailsForm
    ? formValues?.instrument_number || formValues?.booking_page
    : formValues?.recordingInformation;
  const currentLegal = isRecordingDetailsForm
    ? formValues?.legal_property_description
    : formValues?.legalDescriptionLong;

  const ocrRecordingDate = extracted?.recording_date || null;
  const ocrRecordingInfo =
    extracted?.instrument_number || extracted?.recording_info || null;
  const ocrLegal = extracted?.legal_property_description || null;

  const candidates = [
    {
      key: "recording_date",
      label: "Recording Date",
      ocrValue: ocrRecordingDate,
      currentValue: currentRecordingDate,
    },
    {
      key: "instrument_number",
      label: "Instrument Number",
      ocrValue: ocrRecordingInfo,
      currentValue: currentRecordingInfo,
    },
    {
      key: "description_of_property",
      label: "Description of Real Estate",
      ocrValue: extracted?.description_of_property || null,
      currentValue: isRecordingDetailsForm
        ? formValues?.description_of_property
        : formValues?.legalDescriptionShort,
    },
    {
      key: "legal_property_description",
      label: "Legal Property Description",
      ocrValue: ocrLegal,
      currentValue: currentLegal,
    },
  ];

  return candidates
    .filter(
      ({ ocrValue }) =>
        ocrValue !== null && ocrValue !== undefined && ocrValue !== "",
    )
    .filter(
      ({ key, ocrValue, currentValue }) =>
        !currentValue || !uccOcrValuesEquivalent(key, ocrValue, currentValue),
    )
    .filter(
      ({ key, currentValue }) =>
        !["description_of_property", "legal_property_description"].includes(key) ||
        uccOcrMayOfferDescription(key, currentValue, prefillData),
    )
    .map(({ key, label, ocrValue, currentValue }) => ({
      key,
      label,
      ocrValue,
      currentValue: currentValue || "",
    }));
};

export const buildUccOcrAppliedFormValues = (
  currentValues = {},
  prefillData = {},
  { formVariant = "termination" } = {},
) => {
  const extracted = prefillData?.extracted || {};
  const nextValues = { ...currentValues };
  const isRecordingDetailsForm = formVariant === "recordingDetails";

  if (extracted.recording_date) {
    const normalizedDate = normalizeUccRecordingDateForInput(
      extracted.recording_date,
    );

    if (isRecordingDetailsForm) {
      nextValues.recording_date = normalizedDate;
    } else if (formVariant === "terminationIso") {
      nextValues.recordingDate = normalizedDate;
    } else {
      nextValues.recordingDate = getPropertyRecordingDateMMDDYYYY(
        { recording_date: extracted.recording_date },
        "/",
      );
    }
  }

  const recordingInfo =
    extracted.instrument_number || extracted.recording_info || null;
  if (recordingInfo) {
    if (isRecordingDetailsForm) {
      nextValues.instrument_number = recordingInfo;
      nextValues.booking_page = "";
    } else {
      nextValues.recordingInformation = recordingInfo;
    }
  }

  const legalField = isRecordingDetailsForm
    ? "legal_property_description"
    : "legalDescriptionLong";
  if (
    extracted.legal_property_description &&
    uccOcrMayOfferDescription(
      "legal_property_description",
      currentValues?.[legalField],
      prefillData,
    )
  ) {
    nextValues[legalField] = extracted.legal_property_description;
  }

  const shortField = isRecordingDetailsForm
    ? "description_of_property"
    : "legalDescriptionShort";
  if (
    extracted.description_of_property &&
    uccOcrMayOfferDescription(
      "description_of_property",
      currentValues?.[shortField],
      prefillData,
    )
  ) {
    nextValues[shortField] = extracted.description_of_property;
  }

  return nextValues;
};

export const applyUccOcrValuesToForm = (
  setFieldValue,
  prefillData = {},
  { formVariant = "termination", currentValues = {} } = {},
) => {
  const extracted = prefillData?.extracted || {};
  if (!extracted || typeof setFieldValue !== "function") {
    return;
  }

  const nextValues = buildUccOcrAppliedFormValues(currentValues, prefillData, {
    formVariant,
  });

  Object.entries(nextValues).forEach(([fieldName, fieldValue]) => {
    setFieldValue(fieldName, fieldValue);
  });
};

export const applyUccOcrToPropertyData = (
  propertyData = {},
  prefillData = {},
) => {
  const extracted = prefillData?.extracted || {};
  const next = { ...propertyData };

  if (extracted.recording_date) {
    const normalizedDate = normalizeUccRecordingDateForInput(
      extracted.recording_date,
    );
    next.recording_date = normalizedDate || extracted.recording_date;
    next.recordingDate = next.recording_date;
  }

  const recordingInfo =
    extracted.instrument_number || extracted.recording_info || null;
  if (recordingInfo) {
    next.instrument_number = recordingInfo;
    next.booking_page = "";
  }

  if (
    extracted.legal_property_description &&
    uccOcrMayOfferDescription(
      "legal_property_description",
      next.legal_property_description,
      prefillData,
    )
  ) {
    next.legal_property_description = extracted.legal_property_description;
  }

  if (
    extracted.description_of_property &&
    uccOcrMayOfferDescription(
      "description_of_property",
      next.description_of_property,
      prefillData,
    )
  ) {
    next.description_of_property = extracted.description_of_property;
  }

  return next;
};

// dropdown option with first name and last name of professionals in editProperty -> bindProfessional
export const generateProfessionalDropdownOptions = (arr, type) => {
  return arr?.length > 0
    ? arr
        ?.filter((item) => item?.professional_type?.type === type)
        ?.map((filteredItem) => ({
          key: filteredItem?.id,
          label: `${filteredItem?.first_name} ${filteredItem?.last_name}`,
          value: filteredItem?.id,
          is_editable: filteredItem?.is_editable,
        }))
    : [];
};

export const getObjLength = (data) => {
  let dd = data !== undefined && data !== null ? data : {};
  return Object.keys(dd)?.length > 0 ? true : false;
};

// Finds the first error path in a Formik/Yup errors object.
// Returns a dotted path like "first_name" or "address.city" or "owners.0.name".
export const getFirstFormikErrorPath = (errObj, prefix = "") => {
  if (!errObj || typeof errObj !== "object") return null;
  const keys = Object.keys(errObj);
  for (const key of keys) {
    const value = errObj[key];
    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "string" && value.trim().length > 0) return path;

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const nested = getFirstFormikErrorPath(value[i], `${path}.${i}`);
        if (nested) return nested;
      }
      continue;
    }

    if (value && typeof value === "object") {
      const nested = getFirstFormikErrorPath(value, path);
      if (nested) return nested;
    }
  }
  return null;
};

// Scrolls and focuses the input for a given Formik field path.
export const scrollToFormikField = (fieldPath, options = {}) => {
  if (typeof window === "undefined" || !fieldPath) return;

  const {
    behavior = "smooth",
    block = "center",
    focusDelayMs = 50,
    root = document,
  } = options || {};

  const el =
    root.querySelector?.(`[name="${fieldPath}"]`) ||
    root.getElementById?.(fieldPath);

  if (!el || typeof el.scrollIntoView !== "function") return;

  el.scrollIntoView({ behavior, block });
  window.setTimeout(() => {
    if (typeof el.focus === "function") el.focus();
  }, focusDelayMs);
};

// Convenience helper: mark first error as touched, then scroll to it.
export const scrollToFirstFormikError = ({ errors, setFieldTouched, root }) => {
  const firstErrorPath = getFirstFormikErrorPath(errors);
  if (!firstErrorPath) return null;

  if (typeof setFieldTouched === "function") {
    setFieldTouched(firstErrorPath, true);
  }

  scrollToFormikField(firstErrorPath, { root });
  return firstErrorPath;
};

// get the name of the document from url
export const getURLName = (url = "") => {
  const urlArr = url?.split("/");
  return urlArr?.[urlArr?.length - 1];
};

// get Label-value pair from any array of object
export const getLabelValueFromArray = (arr, labelKey, valueKey) => {
  return arr?.map((item) => ({
    label: item?.[labelKey],
    value: item?.[valueKey],
  }));
};

// to get individual value of streetnumber, address, city, state, zipcode
export const getAddressFromGoogleAutoComplete = (
  address,
  key = "street_number",
) => {
  return address
    ? address?.address_components?.find((index) => index?.types?.includes(key))
      ? address?.address_components?.find((index) =>
          index?.types?.includes(key),
        )?.long_name
      : ""
    : "";
};

export const getUsStateOptions = (stateList = []) => {
  const source = stateList?.length ? stateList : USA_STATES;

  return source.map((state) => ({
    name: state.name,
    code: state.code || state.state_code,
  }));
};

export const normalizeUsStateValue = (
  value,
  stateList = [],
  asName = false,
) => {
  if (!value) {
    return "";
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }

  const options = getUsStateOptions(stateList);
  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();
  const match = options.find(
    (state) =>
      (state.code && state.code.toUpperCase() === upper) ||
      (state.name && state.name.toLowerCase() === lower),
  );

  if (asName) {
    return match?.name || trimmed;
  }

  return match?.code || match?.name || trimmed;
};

export const getUsStateNameOptions = (stateList = []) => {
  const source = stateList?.length ? stateList : USA_STATES;

  return source.map((state) => ({
    name: state.name,
  }));
};

export const parsePropertyJsonResponse = (jsonResponse) => {
  if (!jsonResponse) {
    return {};
  }

  if (typeof jsonResponse === "string") {
    try {
      return JSON.parse(jsonResponse) || {};
    } catch {
      return {};
    }
  }

  return jsonResponse;
};

export const resolvePropertyStateFipsCode = (
  propertyRecord = {},
  statesCodeList = {},
) => {
  const jsonResponse = parsePropertyJsonResponse(propertyRecord?.json_response);
  const direct =
    propertyRecord?.state_code ||
    jsonResponse?.mm_fips_state_code ||
    jsonResponse?.state_code;

  if (direct !== undefined && direct !== null && String(direct).trim() !== "") {
    return String(direct);
  }

  const stateValue = propertyRecord?.state || jsonResponse?.sa_site_state || "";
  const normalizedState = normalizeUsStateValue(stateValue);

  if (normalizedState && getObjLength(statesCodeList)) {
    const match = Object.entries(statesCodeList).find(
      ([, abbr]) =>
        String(abbr).toUpperCase() === String(normalizedState).toUpperCase(),
    );

    if (match) {
      return String(match[0]);
    }
  }

  return "";
};

export const resolvePropertyMuniCode = (
  propertyRecord = {},
  jsonResponse = {},
) => {
  const parsed = parsePropertyJsonResponse(jsonResponse);
  const direct =
    propertyRecord?.muni_code || parsed?.mm_fips_muni_code || parsed?.muni_code;

  if (direct !== undefined && direct !== null && String(direct).trim() !== "") {
    let muni = String(direct).trim();
    if (/^\d+$/.test(muni) && muni.length > 0 && muni.length < 3) {
      muni = muni.padStart(3, "0");
    }
    return muni;
  }

  const countyCode = propertyRecord?.county_code || parsed?.county_code;
  const stateCode =
    propertyRecord?.state_code ||
    parsed?.mm_fips_state_code ||
    resolvePropertyStateFipsCode(propertyRecord, {});

  if (countyCode && stateCode) {
    const county = String(countyCode);
    const state = String(stateCode);
    if (county.startsWith(state) && county.length > state.length) {
      return county.slice(state.length);
    }
  }

  return "";
};

export const isRiskMeasureWorkspaceProperty = (property = {}) => {
  const tier = String(property?.product_tier || property?.category || "")
    .toLowerCase()
    .trim();

  return Boolean(
    property?.from_risk_measure ||
    tier === "basic_monitoring" ||
    tier === "enhanced_monitoring",
  );
};

const isTruthyApiFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

/** Unpaid Complete Protection already exists for this parcel — resume pay only. */
export const shouldSkipAddPropertyOnUpgrade = (property = {}) =>
  isTruthyApiFlag(property?.skip_add_property_on_upgrade);

export const hasCompleteProtectionAwaitingPayment = (property = {}) =>
  isTruthyApiFlag(property?.complete_protection_awaiting_payment);

/** DB properties.id for payment APIs when CP was already created unpaid. */
export const resolveCompleteProtectionPropertyIdForUpgrade = (
  property = {},
) => {
  if (!getObjLength(property)) {
    return null;
  }

  // Unpaid CP row itself: use its own DB id when the flag is set.
  if (
    hasCompleteProtectionAwaitingPayment(property) &&
    !isRiskMeasureWorkspaceProperty(property) &&
    property?.property_id
  ) {
    return property.property_id;
  }

  return property?.property_id || null;
};

/**
 * Prefer complete_protection_property_id for upgrade payment payloads so we
 * do not re-create the property when an unpaid CP row already exists.
 */
export const preparePropertyForUpgradePayment = (property = {}) => {
  const normalized = normalizePropertyForUpgradeFlow(property);
  const paymentPropertyId =
    resolveCompleteProtectionPropertyIdForUpgrade(property);
  if (!paymentPropertyId) {
    return normalized;
  }

  return {
    ...normalized,
    property_id: paymentPropertyId,
  };
};

export const resolveRiskReportIdFromProperty = (property = {}) => {
  if (!getObjLength(property)) {
    return null;
  }

  if (isRiskMeasureWorkspaceProperty(property) && property?.id) {
    return property.id;
  }

  return property?.risk_report_id || property?.report_id || null;
};

/** Build admin create payload using fields already on the stored property row. */
export const buildAdminPropertyCreatePayloadFromStored = (
  storedProperty = {},
  userId,
  options = {},
) => {
  const built = buildGenerateQuestionsPayload(storedProperty, userId, options);
  const riskReportId = resolveRiskReportIdFromProperty(storedProperty);
  const mergedJson = {
    ...parsePropertyJsonResponse(storedProperty?.ttb_details),
    ...parsePropertyJsonResponse(storedProperty?.property_details),
    ...parsePropertyJsonResponse(storedProperty?.json_response),
  };
  const json_response = getObjLength(mergedJson)
    ? enrichPropertyJsonResponse(mergedJson, storedProperty)
    : built.json_response;

  return {
    ...built,
    state_code: storedProperty?.state_code || built.state_code,
    property_id: storedProperty?.property_id || built.property_id,
    muni_code:
      storedProperty?.muni_code ||
      built.muni_code ||
      resolvePropertyMuniCode(storedProperty, json_response),
    sa_x_coord: storedProperty?.sa_x_coord ?? built.sa_x_coord,
    sa_y_coord: storedProperty?.sa_y_coord ?? built.sa_y_coord,
    latitude: storedProperty?.latitude ?? built.latitude,
    longitude: storedProperty?.longitude ?? built.longitude,
    json_response,
    country: storedProperty?.country || built.country || "USA",
    price: storedProperty?.price ?? built.price ?? 0,
    ...(riskReportId ? { risk_report_id: riskReportId } : {}),
    ...options?.extra,
  };
};

export const buildPropertySiteAddress = (
  propertyRecord = {},
  jsonResponse = {},
) => {
  const parsed = parsePropertyJsonResponse(jsonResponse);
  const existing =
    parsed?.v_site_address ||
    parsed?.site_address ||
    propertyRecord?.address?.split?.(",")?.[0]?.trim() ||
    "";

  if (String(existing).trim()) {
    return String(existing).trim();
  }

  const street = [
    parsed?.sa_site_house_nbr,
    parsed?.sa_site_fraction,
    parsed?.sa_site_dir,
    parsed?.sa_site_street_name,
    parsed?.sa_site_post_dir,
    parsed?.sa_site_suf,
    propertyRecord?.street_number,
    propertyRecord?.site_route,
  ]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean)
    .join(" ")
    .trim();

  return street;
};

/** Copy shown when Enhanced Monitoring cannot use parcel-only / no-site-address properties. */
export const ENHANCED_MONITORING_PARCEL_UNAVAILABLE_MESSAGE =
  "This feature is not currently available because the scan results are considered unreliable. Matching based solely on a property’s street address produces too many false positives.";

/**
 * True when Benutech/property data has a street site address usable for MLS-based
 * Enhanced Monitoring scans (not blank / Raw Parcel-only).
 */
export const propertyHasUsableSiteAddress = (
  propertyRecord = {},
  jsonResponse = null,
) => {
  const siteAddress = buildPropertySiteAddress(
    propertyRecord,
    jsonResponse ?? propertyRecord?.json_response ?? {},
  );
  const trimmed = String(siteAddress || "").trim();
  if (!trimmed) {
    return false;
  }
  if (/^raw\s*parcel\b/i.test(trimmed)) {
    return false;
  }
  return /^\d/.test(trimmed);
};

export const enrichPropertyJsonResponse = (
  jsonResponse,
  propertyRecord = {},
) => {
  const parsed = parsePropertyJsonResponse(jsonResponse);
  if (!getObjLength(parsed)) {
    return parsed;
  }

  const enriched = { ...parsed };
  const siteAddress = buildPropertySiteAddress(propertyRecord, enriched);

  if (siteAddress) {
    enriched.v_site_address = siteAddress;
    if (!enriched.site_address) {
      enriched.site_address = siteAddress;
    }
  }

  if (!enriched.sa_property_id && propertyRecord?.property_id) {
    enriched.sa_property_id = propertyRecord.property_id;
  }

  return enriched;
};

/** Normalize admin workspace property rows for upgrade / generate-questions APIs. */
export const normalizePropertyForUpgradeFlow = (
  property = {},
  statesCodeList = {},
) => {
  if (!getObjLength(property)) {
    return property;
  }

  const propertyId = property.property_id;
  const mergedSource = {
    ...parsePropertyJsonResponse(property.ttb_details),
    ...parsePropertyJsonResponse(property.property_details),
    ...parsePropertyJsonResponse(property.asset_snapshot?.property_details),
    ...parsePropertyJsonResponse(property.json_response),
  };
  const json_response = enrichPropertyJsonResponse(
    getObjLength(mergedSource) ? mergedSource : null,
    { ...property, property_id: propertyId },
  );
  const directStateCode =
    property.state_code ||
    property.state_id?.state_code ||
    (typeof property.state_id === "string" ? property.state_id : null);
  const state_code =
    directStateCode ||
    resolvePropertyStateFipsCode(
      { ...property, property_id: propertyId, json_response },
      statesCodeList,
    ) ||
    json_response?.mm_fips_state_code ||
    null;

  const normalizedJson = getObjLength(json_response)
    ? {
        ...json_response,
        ...(state_code && !json_response.mm_fips_state_code
          ? { mm_fips_state_code: state_code }
          : {}),
      }
    : state_code
      ? { mm_fips_state_code: state_code }
      : json_response;

  return {
    ...property,
    property_id: propertyId,
    state_code,
    muni_code:
      property.muni_code || resolvePropertyMuniCode(property, normalizedJson),
    json_response: normalizedJson,
  };
};

export const buildPropertySearchAddress = (
  values = {},
  otherPropertyValues = {},
) => {
  const fromAutocomplete = otherPropertyValues?.address?.trim?.();
  const fromFields =
    `${values?.street_number || ""} ${values?.site_route || ""}`.trim();

  return abbreviateStreetForPropertySearch(fromAutocomplete || fromFields);
};

const STREET_DIRECTION_ABBR = {
  north: "N",
  south: "S",
  east: "E",
  west: "W",
  northeast: "NE",
  northwest: "NW",
  southeast: "SE",
  southwest: "SW",
};

const STREET_SUFFIX_ABBR = {
  court: "Ct",
  crt: "Ct",
  street: "St",
  avenue: "Ave",
  road: "Rd",
  drive: "Dr",
  lane: "Ln",
  boulevard: "Blvd",
  circle: "Cir",
  place: "Pl",
  terrace: "Ter",
  parkway: "Pkwy",
  highway: "Hwy",
  trail: "Trl",
  way: "Wy",
};

const isStreetNumberToken = (token = "") =>
  /^\d+[A-Za-z]?(-\d+[A-Za-z]?)?$/.test(String(token).trim());

export const abbreviateStreetForPropertySearch = (line = "") => {
  const normalized = String(line || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!normalized) {
    return "";
  }

  const tokens = normalized.split(" ");
  const count = tokens.length;
  let directionIndex = 0;
  if (count > 1 && isStreetNumberToken(tokens[0])) {
    directionIndex = 1;
  }

  return tokens
    .map((token, index) => {
      const lower = token.toLowerCase().replace(/\.$/, "");
      if (index === directionIndex && STREET_DIRECTION_ABBR[lower]) {
        return STREET_DIRECTION_ABBR[lower];
      }
      if (index === count - 1 && STREET_SUFFIX_ABBR[lower]) {
        return STREET_SUFFIX_ABBR[lower];
      }
      return token;
    })
    .join(" ");
};

export const normalizePropertySearchPayload = (payload = {}) => {
  const siteRoute = abbreviateStreetForPropertySearch(payload.site_route || "");
  const address = abbreviateStreetForPropertySearch(
    payload.address ||
      `${payload.street_number || ""} ${payload.site_route || ""}`.trim(),
  );

  return {
    ...payload,
    state: normalizeUsStateValue(payload.state) || payload.state || "",
    site_route: siteRoute || payload.site_route || "",
    address,
  };
};

export const buildGenerateQuestionsPayload = (
  selectedProperty = {},
  userId,
  options = {},
) => {
  const propertyData = enrichPropertyJsonResponse(
    selectedProperty?.json_response ??
      selectedProperty?.property_details ??
      selectedProperty?.ttb_details,
    selectedProperty,
  );
  const stateCode = resolvePropertyStateFipsCode(
    selectedProperty,
    options?.statesCodeList,
  );
  const muniCode = resolvePropertyMuniCode(selectedProperty, propertyData);
  const saXCoord =
    propertyData?.sa_x_coord ??
    selectedProperty?.sa_x_coord ??
    selectedProperty?.longitude;
  const saYCoord =
    propertyData?.sa_y_coord ??
    selectedProperty?.sa_y_coord ??
    selectedProperty?.latitude;
  const address =
    selectedProperty?.address ||
    buildPropertySiteAddress(selectedProperty, propertyData);

  return {
    state_code: stateCode,
    property_id: selectedProperty?.property_id,
    muni_code: muniCode,
    sa_y_coord: saYCoord,
    sa_x_coord: saXCoord,
    user_id: userId,
    address,
    country: selectedProperty?.country || "USA",
    city: selectedProperty?.city,
    state: selectedProperty?.state,
    zip: selectedProperty?.zip,
    latitude: selectedProperty?.latitude ?? saYCoord,
    longitude: selectedProperty?.longitude ?? saXCoord,
    bed_rooms: selectedProperty?.bed_rooms,
    baths: selectedProperty?.baths,
    sqft: selectedProperty?.sqft,
    year_built: selectedProperty?.year_built,
    json_response: propertyData,
    price: selectedProperty?.price ?? 0,
    county: selectedProperty?.county,
    street_number: selectedProperty?.street_number,
    site_route: selectedProperty?.site_route,
    ...options?.extra,
  };
};

export const mergeGenerateQuestionsPayload = (
  selectedProperty = {},
  userId,
  existingPayload = {},
  options = {},
) => {
  const built = buildGenerateQuestionsPayload(
    selectedProperty,
    userId,
    options,
  );
  const mergedJsonResponse = enrichPropertyJsonResponse(
    getObjLength(existingPayload?.json_response)
      ? existingPayload.json_response
      : built.json_response,
    { ...selectedProperty, ...existingPayload },
  );

  return {
    ...built,
    ...existingPayload,
    state_code: existingPayload?.state_code || built.state_code,
    property_id: existingPayload?.property_id || built.property_id,
    muni_code: existingPayload?.muni_code || built.muni_code,
    sa_x_coord: existingPayload?.sa_x_coord ?? built.sa_x_coord,
    sa_y_coord: existingPayload?.sa_y_coord ?? built.sa_y_coord,
    json_response: mergedJsonResponse,
    user_id: existingPayload?.user_id || built.user_id,
    country: existingPayload?.country || built.country,
    state: existingPayload?.state || built.state,
    latitude: existingPayload?.latitude ?? built.latitude,
    longitude: existingPayload?.longitude ?? built.longitude,
    price: existingPayload?.price ?? built.price,
    address:
      existingPayload?.address ||
      built.address ||
      buildPropertySiteAddress(
        { ...selectedProperty, ...existingPayload },
        mergedJsonResponse,
      ),
  };
};

// to get individual value of streetnumber, address, city, state, zipcode with short_names
export const getShortNameAddressFromGoogleAutoComplete = (
  address,
  key = "street_number",
) => {
  return address
    ? address?.address_components?.find((index) => index?.types?.includes(key))
      ? address?.address_components?.find((index) =>
          index?.types?.includes(key),
        )?.short_name
      : ""
    : "";
};

export const getServiceProviderPlaceValues = (place, stateList = []) => {
  if (!place) {
    return {};
  }

  const streetNumber = getAddressFromGoogleAutoComplete(place, "street_number");
  const route = getAddressFromGoogleAutoComplete(place, "route");
  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  const formattedStreet = (place?.formatted_address || "")
    .split(",")[0]
    ?.trim();

  return {
    ofc_address: street || formattedStreet || "",
    ofc_city:
      getAddressFromGoogleAutoComplete(place, "locality") ||
      getAddressFromGoogleAutoComplete(place, "sublocality") ||
      "",
    ofc_state: normalizeUsStateValue(
      getShortNameAddressFromGoogleAutoComplete(
        place,
        "administrative_area_level_1",
      ),
      stateList,
    ),
    ofc_zip: getAddressFromGoogleAutoComplete(place, "postal_code") || "",
    ofc_suite: getAddressFromGoogleAutoComplete(place, "subpremise") || "",
  };
};

export const getAdminCreateUserPlaceValues = (place, stateList = []) => {
  if (!place) {
    return {};
  }

  const streetNumber = getAddressFromGoogleAutoComplete(place, "street_number");
  const route = getAddressFromGoogleAutoComplete(place, "route");
  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  const formattedStreet = (place?.formatted_address || "")
    .split(",")[0]
    ?.trim();

  let city =
    getAddressFromGoogleAutoComplete(place, "locality") ||
    getAddressFromGoogleAutoComplete(place, "postal_town") ||
    getAddressFromGoogleAutoComplete(place, "sublocality") ||
    getAddressFromGoogleAutoComplete(place, "sublocality_level_1") ||
    getAddressFromGoogleAutoComplete(place, "neighborhood") ||
    getAddressFromGoogleAutoComplete(place, "administrative_area_level_3") ||
    "";

  let state = normalizeUsStateValue(
    getShortNameAddressFromGoogleAutoComplete(
      place,
      "administrative_area_level_1",
    ),
    stateList,
    true,
  );

  let zip =
    getAddressFromGoogleAutoComplete(place, "postal_code")?.split("-")[0] || "";

  const formatted = place?.formatted_address || "";
  let addressValue = street || formattedStreet;

  if (formatted && (!city || !state || !zip || !addressValue)) {
    const parts = formatted.split(",").map((part) => part.trim());
    const count = parts.length;

    if (count >= 3) {
      const stateVal = parts[count - 2] || "";
      const cityFromFormat = parts[count - 3] || "";
      const streetFromFormat = parts[count - 4] ?? parts[0] ?? "";
      const stateSplit = stateVal.split(/\s+/).filter(Boolean);

      if (!city && cityFromFormat) {
        city = cityFromFormat;
      }

      if (!zip && stateSplit.length > 1) {
        const maybeZip = stateSplit[stateSplit.length - 1];
        if (/^\d{5}(-\d{4})?$/.test(maybeZip)) {
          zip = maybeZip.split("-")[0];
        }
      }

      if (!state) {
        state = normalizeUsStateValue(
          stateSplit[0] || stateSplit[1] || "",
          stateList,
          true,
        );
      }

      if (!addressValue && streetFromFormat) {
        addressValue = streetFromFormat;
      }
    }
  }

  const patch = {};
  if (addressValue) {
    patch.address = addressValue;
  }
  if (city) {
    patch.city = city;
  }
  if (state) {
    patch.state = state;
  }
  if (zip) {
    patch.zip = zip;
  }

  const unit = getAddressFromGoogleAutoComplete(place, "subpremise");
  if (unit) {
    patch.unit_no = unit;
  }

  return patch;
};

export const ADMIN_CREATE_USER_ADDRESS_FIELDS = [
  "address",
  "city",
  "state",
  "zip",
  "unit_no",
];

export const resolvePropertySearchStateFormCode = (
  stateValue,
  stateList = [],
) => {
  if (!stateValue) {
    return "";
  }

  const code = normalizeUsStateValue(stateValue, stateList, false);
  const options = getUsStateOptions(stateList);
  const lower = String(stateValue).trim().toLowerCase();
  const match = options.find(
    (state) =>
      state.code === code ||
      state.code === stateValue ||
      state.name?.toLowerCase() === lower,
  );

  return match?.code || code || String(stateValue).trim();
};

export const buildPropertySearchFormValues = (param = {}, stateList = []) => ({
  street_number: param.street_number || "",
  site_route: param.site_route || "",
  city: param.city || "",
  state: resolvePropertySearchStateFormCode(param.state, stateList),
  zip: param.zip || "",
  unit_no: param.unit_no || "",
  county: param.county || "",
});

export const getPropertySearchPlaceValuesFromGoogle = (
  place,
  stateList = [],
) => {
  const result = getPropertySearchPlaceValuesFromGooglePlace(place, stateList);
  return {
    ...result,
    formValues: buildPropertySearchFormValues(result.param, stateList),
  };
};

const getPropertySearchPlaceValuesFromGooglePlace = (place, stateList = []) => {
  if (!place) {
    return { param: {}, fixParams: {} };
  }

  const formatted = place?.formatted_address || "";
  const parts = formatted.split(",").map((part) => part.trim());
  const count = parts.length;

  const streetNumber = getAddressFromGoogleAutoComplete(place, "street_number");
  const route = getAddressFromGoogleAutoComplete(place, "route");
  const streetFromComponents = [streetNumber, route]
    .filter(Boolean)
    .join(" ")
    .trim();
  const streetAdd =
    streetFromComponents ||
    (count >= 4 ? parts[count - 4] : parts[0] || "").trim();

  let city =
    getAddressFromGoogleAutoComplete(place, "locality") ||
    getAddressFromGoogleAutoComplete(place, "postal_town") ||
    getAddressFromGoogleAutoComplete(place, "sublocality") ||
    getAddressFromGoogleAutoComplete(place, "sublocality_level_1") ||
    getAddressFromGoogleAutoComplete(place, "neighborhood") ||
    getAddressFromGoogleAutoComplete(place, "administrative_area_level_3") ||
    "";

  if (!city && count >= 3) {
    city = (parts[count - 3] || "").trim();
  }

  let state =
    getShortNameAddressFromGoogleAutoComplete(
      place,
      "administrative_area_level_1",
    ) || "";

  if (!state && count >= 2) {
    const stateSplit = (parts[count - 2] || "").split(/\s+/).filter(Boolean);
    state =
      normalizeUsStateValue(stateSplit[0] || stateSplit[1] || "", stateList) ||
      stateSplit[0] ||
      "";
  } else {
    state = normalizeUsStateValue(state, stateList) || state;
  }

  let zip =
    getAddressFromGoogleAutoComplete(place, "postal_code")?.split("-")[0] || "";

  if (!zip && count >= 2) {
    const stateSplit = (parts[count - 2] || "").split(/\s+/).filter(Boolean);
    const maybeZip = stateSplit[stateSplit.length - 1];
    if (/^\d{5}(-\d{4})?$/.test(maybeZip)) {
      zip = maybeZip.split("-")[0];
    }
  }

  const unit_no = getAddressFromGoogleAutoComplete(place, "subpremise") || "";
  const countyRaw = getShortNameAddressFromGoogleAutoComplete(
    place,
    "administrative_area_level_2",
  );
  const county = countyRaw?.replace(/County/i, "").trim() || "";

  const latitude =
    typeof place?.geometry?.location?.lat === "function"
      ? place.geometry.location.lat()
      : place?.geometry?.location?.lat;
  const longitude =
    typeof place?.geometry?.location?.lng === "function"
      ? place.geometry.location.lng()
      : place?.geometry?.location?.lng;

  const country = count > 0 ? parts[count - 1].trim() : "";

  const param = {};
  let parsedStreetNumber = streetNumber;
  let parsedSiteRoute = route;

  if (!parsedStreetNumber && streetAdd) {
    const streetMatch = streetAdd.match(/^(\d+)\s+(.*)$/);
    if (streetMatch) {
      parsedStreetNumber = streetMatch[1];
      parsedSiteRoute = streetMatch[2];
    } else if (!parsedSiteRoute) {
      parsedSiteRoute = streetAdd;
    }
  }

  if (parsedStreetNumber) {
    param.street_number = parsedStreetNumber;
  }
  if (parsedSiteRoute) {
    param.site_route = parsedSiteRoute;
  }
  if (city) {
    param.city = city;
  }
  if (state) {
    param.state = state;
  }
  if (zip) {
    param.zip = zip;
  }
  if (unit_no) {
    param.unit_no = unit_no;
  }
  if (county) {
    param.county = county;
  }
  if (streetAdd) {
    param.address = streetAdd;
  }

  const fixParams = {};
  if (country) {
    fixParams.country = country;
  }
  if (latitude != null && latitude !== "") {
    fixParams.latitude = latitude;
  }
  if (longitude != null && longitude !== "") {
    fixParams.longitude = longitude;
  }
  if (streetAdd) {
    fixParams.address = streetAdd;
  }

  return { param, fixParams };
};

export const buildAdminCreateUserAddressSearchPayload = (
  values = {},
  stateList = [],
) => {
  const address = String(values.address || "").trim();
  const streetMatch = address.match(/^(\d+)\s+(.*)$/);

  return {
    street_number: streetMatch?.[1] || "",
    site_route: streetMatch?.[2] || address,
    city: String(values.city || "").trim(),
    state:
      normalizeUsStateValue(values.state, stateList, false) ||
      String(values.state || "").trim(),
    zip: String(values.zip || "").trim(),
    unit_no: String(values.unit_no || "").trim(),
    address,
  };
};

export const applyAdminCreateUserPlaceSelection = (
  formik,
  place,
  stateList = [],
  onPlaceSideEffect,
) => {
  if (!formik || !place) {
    return;
  }

  const patch = getAdminCreateUserPlaceValues(place, stateList);

  ADMIN_CREATE_USER_ADDRESS_FIELDS.forEach((field) => {
    if (patch[field] !== undefined && patch[field] !== "") {
      formik.setFieldValue?.(field, patch[field]);
    }
  });

  ADMIN_CREATE_USER_ADDRESS_FIELDS.forEach((field) => {
    if (patch[field] !== undefined && patch[field] !== "") {
      formik.setFieldTouched?.(field, true, false);
    }
  });

  if (formik.validateForm) {
    const nextValues = { ...formik.values, ...patch };
    formik.validateForm(nextValues).then((errors) => {
      formik.setErrors?.(errors);
    });
  }

  try {
    onPlaceSideEffect?.(place);
  } catch (error) {
    console.error("Admin create user place side effect failed:", error);
  }
};

export const getAdminCreateUserPropertyFormValues = (
  propertyRecord = {},
  stateList = [],
) => {
  const json = enrichPropertyJsonResponse(
    propertyRecord?.json_response,
    propertyRecord,
  );

  const streetAddress =
    String(propertyRecord?.address || "")
      .split(",")[0]
      ?.trim() ||
    buildPropertySiteAddress(propertyRecord, json) ||
    "";

  const city = String(propertyRecord?.city || json?.sa_site_city || "").trim();

  const state = normalizeUsStateValue(
    propertyRecord?.state || json?.sa_site_state || "",
    stateList,
    true,
  );

  const zipDigits = String(
    propertyRecord?.zip || json?.sa_site_zip || "",
  ).replace(/\D/g, "");
  const zip = zipDigits.slice(0, 5);

  const unit_no = String(
    propertyRecord?.unit_no || json?.sa_site_unit || "",
  ).trim();

  const patch = {};
  if (streetAddress) {
    patch.address = streetAddress;
  }
  if (city) {
    patch.city = city;
  }
  if (state) {
    patch.state = state;
  }
  if (zip) {
    patch.zip = zip;
  }
  if (unit_no) {
    patch.unit_no = unit_no;
  }

  return patch;
};

export const applyAdminCreateUserPropertySelection = (
  formik,
  propertyRecord = {},
  stateList = [],
  options = {},
) => {
  if (!formik || !getObjLength(propertyRecord)) {
    return {};
  }

  const ownerNames = resolvePropertyOwnerNameFields(propertyRecord);
  const patch = getAdminCreateUserPropertyFormValues(propertyRecord, stateList);
  const skipOwnerNames = Boolean(options.skipOwnerNames);

  if (skipOwnerNames) {
    patch.first_owner_first_name = "";
    patch.first_owner_last_name = "";
    patch.first_name = "";
    patch.last_name = "";
  } else {
    if (ownerNames.first_name) {
      patch.first_owner_first_name = ownerNames.first_name;
    }
    if (ownerNames.last_name) {
      patch.first_owner_last_name = ownerNames.last_name;
    }
  }

  if (!getObjLength(patch)) {
    return {};
  }

  const nextValues = { ...formik.values, ...patch };
  formik.setValues(nextValues, false);

  if (skipOwnerNames) {
    formik.setFieldValue("first_name", "", false);
    formik.setFieldValue("last_name", "", false);
  } else {
    if (patch.first_owner_first_name && !formik.values.first_name?.trim()) {
      formik.setFieldValue("first_name", patch.first_owner_first_name, false);
    }
    if (patch.first_owner_last_name && !formik.values.last_name?.trim()) {
      formik.setFieldValue("last_name", patch.first_owner_last_name, false);
    }
  }

  [
    ...ADMIN_CREATE_USER_ADDRESS_FIELDS,
    "first_owner_first_name",
    "first_owner_last_name",
  ].forEach((field) => {
    if (patch[field] !== undefined && formik.setFieldTouched) {
      formik.setFieldTouched(field, true, false);
    }
  });

  if (typeof formik.validateForm === "function") {
    formik.validateForm(nextValues).then((errors) => {
      if (typeof formik.setErrors === "function") {
        formik.setErrors(errors);
      }
    });
  }

  return patch;
};

export const applyAdminCreateUserPropertyOwners = (
  formik,
  propertyRecord = {},
  stateList = [],
) => applyAdminCreateUserPropertySelection(formik, propertyRecord, stateList);

export const buildServiceProviderPlaceHandler =
  (setFieldValue, stateList = []) =>
  (place) => {
    const patch = getServiceProviderPlaceValues(place, stateList);
    Object.entries(patch).forEach(([field, value]) => {
      setFieldValue(field, value, false);
    });
  };

export const SERVICE_PROVIDER_ADDRESS_FIELDS = [
  "ofc_address",
  "ofc_city",
  "ofc_state",
  "ofc_zip",
  "ofc_suite",
];

export const applyServiceProviderPlaceSelection = (
  formik,
  place,
  stateList = [],
) => {
  if (!formik || !place) {
    return;
  }

  const patch = getServiceProviderPlaceValues(place, stateList);
  formik.setValues({ ...formik.values, ...patch }, true);
  SERVICE_PROVIDER_ADDRESS_FIELDS.forEach((field) => {
    formik.setFieldTouched(field, true, false);
  });
};

export const normalizeSignupEmail = (email = "") =>
  String(email).trim().toLowerCase();

export const trimServiceProviderSignupValues = (values = {}) => {
  const trim = (value) => (typeof value === "string" ? value.trim() : value);

  return {
    ...values,
    first_name: trim(values.first_name),
    last_name: trim(values.last_name),
    company_name: trim(values.company_name),
    ofc_address: trim(values.ofc_address),
    ofc_suite: trim(values.ofc_suite),
    ofc_city: trim(values.ofc_city),
  };
};

export const normalizeServiceProviderSignupPayload = (values = {}) => {
  const payload = trimServiceProviderSignupValues(values);
  const phone = values?.ofc_phone || "";

  payload.email = normalizeSignupEmail(values?.email);
  payload.phone_number = phone;
  payload.ofc_phone = String(phone).replace(/\D/g, "");

  return payload;
};

export const checkFiniteCoordinate = (coordinate) => {
  return !isNaN(coordinate?.lat) && !isNaN(coordinate?.lng);
};

// return url link with green color if url exist
export const getUrlLink = (title, link) => {
  return urlPatternValidation(link) ? (
    <CustomLinkWrapper classes="green-link" title={title} link={link} />
  ) : (
    "-"
  );
};
// return url link with green color and file icon if url exist
export const getUrlLinkandFileIcon = (title, link) => {
  return urlPatternValidation(link) ? (
    <CustomLinkWrapper
      classes="green-link"
      title={title}
      link={link}
      img={link !== "-" ? true : false}
    />
  ) : (
    "-"
  );
};

export const extractTtbFetchKey = (docLink) => {
  if (!docLink || typeof docLink !== "string") return null;
  const match = docLink.match(/fetch_key=([^&"#\s]+)/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const binaryStringToUint8Array = (binaryString) => {
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i) & 0xff;
  }
  return bytes;
};

export const isArrayBufferLike = (value) =>
  value instanceof ArrayBuffer ||
  Object.prototype.toString.call(value) === "[object ArrayBuffer]";

export const parseArrayBufferToJson = (buffer) => {
  if (!isArrayBufferLike(buffer)) return buffer;
  try {
    const text = new TextDecoder().decode(buffer);
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export const isPdfArrayBuffer = (buffer) => {
  if (!isArrayBufferLike(buffer)) return false;
  const view = new Uint8Array(buffer);
  return (
    view.length >= 4 &&
    view[0] === 0x25 &&
    view[1] === 0x50 &&
    view[2] === 0x44 &&
    view[3] === 0x46
  );
};

export const arrayBufferToBase64 = (buffer) => {
  if (!isArrayBufferLike(buffer)) return null;
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const normalizeBase64 = (base64Data) => {
  let normalized = base64Data.includes(",")
    ? base64Data.split(",").pop()
    : base64Data;
  normalized = normalized.trim().replace(/\s/g, "");
  normalized = normalized.replace(/-/g, "+").replace(/_/g, "/");
  const pad = normalized.length % 4;
  if (pad) {
    normalized += "=".repeat(4 - pad);
  }
  return normalized;
};

export const openBlobUrlInNewTab = (blobUrl) => {
  if (!blobUrl || typeof blobUrl !== "string") return false;

  const newTab = window.open(blobUrl, "_blank");
  if (!newTab) return false;

  newTab.opener = null;
  return true;
};

export const revokePdfPreviewUrl = (previewUrl) => {
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
};

export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) {
      reject(new Error("Expected a Blob"));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Unable to read PDF blob."));
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Unable to read PDF blob."));
    reader.readAsDataURL(blob);
  });

/**
 * The native shells expose three channels, in descending order of reliability:
 *
 * 1. `EquityProtectNative` — injected by the React Native shell on every load.
 * 2. `webkit.messageHandlers.*` — real handlers in the legacy Swift app, and
 *    shims in the React Native shell. WKWebView can refuse those shim
 *    assignments, so a message sent only here may silently go nowhere.
 * 3. `ReactNativeWebView` — the raw React Native channel.
 */
const nativeBridgeChannel = (type) => {
  if (typeof window === "undefined" || !type) return null;

  try {
    if (typeof window.EquityProtectNative?.post === "function") return "native";
  } catch {
    // Fall through to the next channel.
  }

  try {
    if (
      typeof window.webkit?.messageHandlers?.[type]?.postMessage === "function"
    )
      return "webkit";
  } catch {
    // Named-property access on messageHandlers can throw.
  }

  try {
    if (typeof window.ReactNativeWebView?.postMessage === "function")
      return "reactNative";
  } catch {
    // No native channel available.
  }

  return null;
};

export const hasNativeBridgeChannel = (type) =>
  nativeBridgeChannel(type) !== null;

/** Send a bridge message over whichever native channel is reachable. */
export const postNativeBridgeMessage = (type, payload = {}) => {
  const channel = nativeBridgeChannel(type);
  if (!channel) return false;

  try {
    if (channel === "native") {
      window.EquityProtectNative.post(type, payload);
      return true;
    }

    if (channel === "webkit") {
      window.webkit.messageHandlers[type].postMessage(payload);
      return true;
    }

    window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    return true;
  } catch {
    return false;
  }
};

export const openPdfUrlViaNativeBridge = (url, filename = "document.pdf") => {
  if (!url || typeof url !== "string") return false;

  return postNativeBridgeMessage("openNativePdf", { url, filename });
};

export const openPdfBlobViaNativeBridge = async (
  blob,
  filename = "document.pdf",
) => {
  if (!(blob instanceof Blob)) return false;
  if (!hasNativeBridgeChannel("openNativePdf")) return false;

  try {
    const dataUrl = await blobToDataUrl(blob);
    const base64 = dataUrl.split(",")[1];
    if (!base64) return false;

    return postNativeBridgeMessage("openNativePdf", { base64, filename });
  } catch {
    return false;
  }
};

/** Open http(s) links outside the in-app WebView when the native bridge is available. */
export const openExternalLink = (url) => {
  if (!url || typeof url !== "string") return false;

  return postNativeBridgeMessage("openExternalUrl", { url });
};

const openPdfBlobInNewTab = (blob, targetWindow = null) => {
  if (!(blob instanceof Blob)) return false;

  const objectUrl = URL.createObjectURL(blob);

  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = objectUrl;
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    return true;
  }

  const pdfWindow = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!pdfWindow) {
    URL.revokeObjectURL(objectUrl);
    return false;
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  return true;
};

export const getTtbPdfBase64FromResponse = (response) => {
  if (!response?.success) return null;
  const data = response?.data;
  if (typeof data === "string") return data;
  if (typeof data?.pdf === "string") return data.pdf;
  if (typeof data?.document === "string") return data.document;
  if (typeof data?.file === "string") return data.file;
  return null;
};

export const openBase64PdfInNewTab = (base64Data, targetWindow = null) => {
  if (!base64Data || typeof base64Data !== "string") return false;

  try {
    const byteCharacters = atob(normalizeBase64(base64Data));
    return openPdfBlobInNewTab(
      new Blob([binaryStringToUint8Array(byteCharacters)], {
        type: "application/pdf",
      }),
      targetWindow,
    );
  } catch {
    return false;
  }
};

const getTtbPdfPayloadFromResponse = (response) => {
  if (response instanceof ArrayBuffer || response instanceof Uint8Array) {
    return response;
  }

  if (typeof Blob !== "undefined" && response instanceof Blob) {
    return response;
  }

  if (!response?.success) return null;

  const data = response?.data;

  if (data instanceof ArrayBuffer || data instanceof Uint8Array) {
    return data;
  }

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return data;
  }

  if (typeof data === "string") {
    if (data.startsWith("%PDF")) {
      return binaryStringToUint8Array(data);
    }
    return data;
  }

  const nested = data?.pdf ?? data?.document ?? data?.file;
  if (typeof nested === "string") {
    if (nested.startsWith("%PDF")) {
      return binaryStringToUint8Array(nested);
    }
    return nested;
  }

  return null;
};

const getTtbPdfBlobFromPayload = (payload) => {
  if (!payload) return null;

  if (payload instanceof Blob) {
    return payload;
  }

  if (payload instanceof ArrayBuffer || payload instanceof Uint8Array) {
    return new Blob(
      [payload instanceof Uint8Array ? payload : new Uint8Array(payload)],
      { type: "application/pdf" },
    );
  }

  if (typeof payload === "string") {
    try {
      const byteCharacters = payload.startsWith("%PDF")
        ? payload
        : atob(normalizeBase64(payload));
      return new Blob([binaryStringToUint8Array(byteCharacters)], {
        type: "application/pdf",
      });
    } catch {
      return null;
    }
  }

  return null;
};

export const createTtbPdfBlobUrlFromResponse = (response) => {
  const payload = getTtbPdfPayloadFromResponse(response);
  const blob = getTtbPdfBlobFromPayload(payload);
  if (!blob) return null;
  return URL.createObjectURL(blob);
};

export const openTtbPdfFromResponse = (response, targetWindow = null) => {
  const payload = getTtbPdfPayloadFromResponse(response);
  const blob = getTtbPdfBlobFromPayload(payload);
  if (!blob) return false;

  return openPdfBlobInNewTab(blob, targetWindow);
};

export const getTtbPdfBlobFromResponse = (response) =>
  getTtbPdfBlobFromPayload(getTtbPdfPayloadFromResponse(response));

// Date fromay by full month name ,Date , Year
export const getDateSeperated = (fulldate) => {
  if (fulldate && fulldate === "-") {
    return null;
  } else {
    let dateString = fulldate.split("/");
    console.log("datestring", dateString);
    const date = new Date(
      dateString[2],
      parseInt(dateString[0]) - 1,
      dateString[1],
    ); // 2009-11-10
    const month = date.toLocaleString("default", { month: "long" });
    // return { month: month, date: dateString[1], year: dateString[2] };
    return month + " " + dateString[1] + " , " + dateString[2];
  }
};

export const redirectSubDomainToMainDomain = (path) => {
  return redirectToAppPath(path);
};

export const censorWord = function (str) {
  if (str.length > 4) {
    return str[0] + str[1] + "*".repeat(str.length - 4) + str.slice(-2);
  } else {
    return str[0] + "*".repeat(str.length - 2) + str.slice(-1);
  }
};

export const censorEmail = function (email) {
  if (email) {
    var arr = email?.split("@");
    return censorWord(arr[0]) + "@" + arr[1];
  } else {
    return email;
  }
};

// return formated phone number
export const getFormatedPhoneNumber = (phone) => {
  return phone?.slice(0, 2) !== "+1"
    ? formatPhoneNumberIntl(phone)
    : formatPhoneNumber(phone);
};

export const blobToImage = (blob) => {
  return new Promise((resolve) => {
    // const url = URL.createObjectURL(blob);
    let img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blob);
      resolve(img);
    };
    img.src = blob;
  });
};

export const numberToWords = (num) => {
  const numList = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
  ];
  return numList[num];
};

export const user_signup_details = () => {
  const raw = localStorage.getItem("userSignupDetails");
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const decodeBase64String = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return atob(value);
  } catch {
    return null;
  }
};

export const encodeBase64String = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  try {
    return btoa(value);
  } catch {
    return null;
  }
};

export const getSignupLoginCredentials = () => {
  const signupDetails = user_signup_details();
  let sessionDetails = null;

  try {
    const storedValue = sessionStorage.getItem("userSignupDetails");
    sessionDetails = storedValue ? JSON.parse(storedValue) : null;
  } catch {
    sessionDetails = null;
  }

  const email =
    signupDetails?.data?.email ||
    signupDetails?.email ||
    sessionDetails?.data?.email ||
    "";
  const passwordToken = sessionDetails?.data?.password;
  const password = passwordToken ? decodeBase64String(passwordToken) : null;

  return { email, password };
};

export const getStoredAuthToken = () =>
  localStorage.getItem("setLoginUserToken") ||
  localStorage.getItem("authToken");

export const hasValidAuthToken = () => {
  const token = getStoredAuthToken();
  return Boolean(token && token !== "null" && token !== "undefined");
};

export const isAuthenticatedSession = () =>
  localStorage.getItem("authorised") === "true" && hasValidAuthToken();

export const clearStoredLoginRedirect = () => {
  localStorage.removeItem("previousPath");
  localStorage.removeItem("previousUserID");
};

export const clearStaleAuthSession = (force = false) => {
  if (force) {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authorised");
    localStorage.removeItem("setLoginUserToken");
    localStorage.removeItem("roles");
    localStorage.removeItem("activeRole");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("agentauthToken");
    localStorage.removeItem("agent_role");
    localStorage.removeItem("agent_id");
    return;
  }

  if (localStorage.getItem("authorised") === "true" && !hasValidAuthToken()) {
    localStorage.removeItem("authorised");
    localStorage.removeItem("authToken");
    localStorage.removeItem("setLoginUserToken");
    localStorage.removeItem("roles");
    localStorage.removeItem("refreshToken");
  }
};

export const applyStoredSignupLoginToken = () => {
  const loginToken = getStoredAuthToken();
  if (!loginToken || loginToken === "null" || loginToken === "undefined") {
    return false;
  }

  localStorage.setItem("authToken", loginToken);
  localStorage.setItem("authorised", "true");
  return true;
};

export const persistSignupUserId = (userId) => {
  if (userId === null || userId === undefined || userId === "") {
    return;
  }

  localStorage.setItem("user_Id", JSON.stringify(userId));
  markSignupInProgress();

  try {
    const existingSignupDetails = JSON.parse(
      localStorage.getItem("userSignupDetails") || "null",
    );
    if (existingSignupDetails && typeof existingSignupDetails === "object") {
      existingSignupDetails.user_id = userId;
      localStorage.setItem(
        "userSignupDetails",
        JSON.stringify(existingSignupDetails),
      );
    }
  } catch {
    // ignore localStorage merge errors
  }

  try {
    const sessionDetails = JSON.parse(
      sessionStorage.getItem("userSignupDetails") || "null",
    );
    if (sessionDetails?.data && typeof sessionDetails.data === "object") {
      sessionDetails.data.user_id = userId;
      sessionStorage.setItem(
        "userSignupDetails",
        JSON.stringify(sessionDetails),
      );
    }
  } catch {
    // ignore sessionStorage merge errors
  }
};

export const parseLocalStorageJson = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    if (value === null || value === "") {
      return fallback;
    }
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/** Persist a validated promo code to localStorage + Plan_Data. */
export const persistStoredPromoCode = (code) => {
  const trimmed = String(code || "").trim();
  if (!trimmed) {
    return;
  }
  localStorage.setItem("promoCode", trimmed);
  try {
    sessionStorage.setItem("ep_carried_promo", trimmed);
  } catch {
    // ignore
  }
  try {
    const planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    localStorage.setItem(
      "Plan_Data",
      JSON.stringify({ ...planData, promo_code: trimmed }),
    );
  } catch {
    // ignore invalid Plan_Data
  }
};

/** Read the active promo from localStorage, session carry, or Plan_Data. */
export const getStoredPromoCode = () => {
  const fromKey = localStorage.getItem("promoCode");
  if (fromKey && String(fromKey).trim()) {
    return String(fromKey).trim();
  }
  try {
    const carried = sessionStorage.getItem("ep_carried_promo");
    if (carried && String(carried).trim()) {
      return String(carried).trim();
    }
  } catch {
    // ignore
  }
  try {
    const planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    if (planData?.promo_code) {
      return String(planData.promo_code).trim();
    }
  } catch {
    // ignore invalid Plan_Data
  }
  return "";
};

/** Remove promo code from localStorage and Plan_Data. */
export const clearStoredPromoCode = () => {
  localStorage.removeItem("promoCode");
  try {
    sessionStorage.removeItem("ep_carried_promo");
  } catch {
    // ignore
  }
  try {
    const planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    if (planData?.promo_code != null) {
      const { promo_code: _removed, ...rest } = planData;
      localStorage.setItem("Plan_Data", JSON.stringify(rest));
    }
  } catch {
    // ignore invalid Plan_Data
  }
};

export const EP_AWARE_SIGNUP_SESSION_KEY = "epAwareSignupSessionId";
export const EP_AWARE_SIGNUP_VERIFY_URL_KEY = "epAwareSignupVerifyUrl";
export const EP_AWARE_SIGNUP_QR_KEY = "epAwareSignupQrCode";

export const persistEpAwareSignupSessionId = (sessionId) => {
  if (sessionId === null || sessionId === undefined || sessionId === "") {
    return;
  }
  sessionStorage.setItem(EP_AWARE_SIGNUP_SESSION_KEY, String(sessionId));
};

export const persistEpAwareSignupArtifacts = ({
  sessionId,
  verifyUrl,
  qrCodeDataUri,
} = {}) => {
  persistEpAwareSignupSessionId(sessionId);
  if (verifyUrl) {
    sessionStorage.setItem(EP_AWARE_SIGNUP_VERIFY_URL_KEY, verifyUrl);
  }
  if (qrCodeDataUri) {
    sessionStorage.setItem(EP_AWARE_SIGNUP_QR_KEY, qrCodeDataUri);
  }
};

export const getEpAwareSignupArtifacts = () => ({
  sessionId: sessionStorage.getItem(EP_AWARE_SIGNUP_SESSION_KEY),
  verifyUrl: sessionStorage.getItem(EP_AWARE_SIGNUP_VERIFY_URL_KEY),
  qrCodeDataUri: sessionStorage.getItem(EP_AWARE_SIGNUP_QR_KEY),
});

export const clearEpAwareSignupSessionId = () => {
  sessionStorage.removeItem(EP_AWARE_SIGNUP_SESSION_KEY);
  sessionStorage.removeItem(EP_AWARE_SIGNUP_VERIFY_URL_KEY);
  sessionStorage.removeItem(EP_AWARE_SIGNUP_QR_KEY);
};

export const getEpAwareSignupSessionId = () =>
  sessionStorage.getItem(EP_AWARE_SIGNUP_SESSION_KEY);

export const sanitizePropertyAddressForEpAware = (property = {}) => {
  if (!property || typeof property !== "object") {
    return {};
  }

  const keys = [
    "address",
    "address2",
    "city",
    "state",
    "zip",
    "country",
    "property_id",
    "display_address",
    "street_number",
    "site_route",
    "latitude",
    "longitude",
    "county",
    "owner_name",
    "owner",
  ];

  return keys.reduce((result, key) => {
    const value = property[key];
    if (
      value === null ||
      value === undefined ||
      typeof value === "function" ||
      typeof value === "object"
    ) {
      return result;
    }

    result[key] = value;
    return result;
  }, {});
};

export const applyEpAwareInviteArtifacts = ({
  verifyUrl,
  qrCodeDataUri,
  setVerifyUrl,
  setQrCodeDataUri,
}) => {
  const resolvedVerifyUrl =
    verifyUrl || getEpAwareSignupArtifacts().verifyUrl || "";
  const resolvedQrCodeDataUri =
    qrCodeDataUri || getEpAwareSignupArtifacts().qrCodeDataUri || "";

  if (resolvedVerifyUrl) {
    setVerifyUrl(resolvedVerifyUrl);
  }
  if (resolvedQrCodeDataUri) {
    setQrCodeDataUri(resolvedQrCodeDataUri);
  }

  return {
    verifyUrl: resolvedVerifyUrl,
    qrCodeDataUri: resolvedQrCodeDataUri,
  };
};

export const hasMidSignupProgress = () => {
  const userId = localStorage.getItem("user_Id");
  const currentStep = localStorage.getItem("currentStep");
  if (!userId || !currentStep) {
    return false;
  }

  return Boolean(
    localStorage.getItem("signup_product_tier") ||
    localStorage.getItem("plain_signup") === "true" ||
    localStorage.getItem("category"),
  );
};

export const SIGNUP_IN_PROGRESS_KEY = "signup_in_progress";

export const markSignupInProgress = () => {
  localStorage.setItem(SIGNUP_IN_PROGRESS_KEY, "1");
};

export const clearSignupInProgressFlag = () => {
  localStorage.removeItem(SIGNUP_IN_PROGRESS_KEY);
};

export const markSignupSessionComplete = () => {
  localStorage.setItem("completed", "1");
  clearSignupInProgressFlag();
};

/**
 * Signup finished: drop the resume markers so protected routes stop treating the
 * session as mid-signup. `user_Id` and `category` stay because post-signup
 * screens still read them.
 */
export const clearSignupResumeMarkers = () => {
  markSignupSessionComplete();
  localStorage.removeItem("currentStep");
  localStorage.removeItem("isResumeSignup");
};

export const isSignupSessionComplete = () =>
  localStorage.getItem("completed") === "1";

export const isIncompleteSignupSession = () => {
  if (isSignupSessionComplete()) {
    return false;
  }

  if (localStorage.getItem(SIGNUP_IN_PROGRESS_KEY) === "1") {
    return true;
  }

  return hasMidSignupProgress();
};

/** Hide public-header My Account on signup routes only; always show elsewhere. */
export const isPublicSignupFlowPath = (pathname = window.location.pathname) => {
  const path = pathname.toLowerCase();

  return (
    path.includes("create-account") ||
    path.includes("setup-account") ||
    path.includes("agent-create-account") ||
    path.includes("attorney-create-account") ||
    path.includes("add-new-users")
  );
};

/** Public-header My Account stays visible on signup routes (login / resume signup). */
export const shouldHideMyAccountButton = () => {
  if (isAuthenticatedSession()) {
    return false;
  }

  return isPublicSignupFlowPath();
};

/** Resume mid-signup from My Account only while on a signup route. */
export const shouldResumeSignupFromMyAccount = () =>
  isPublicSignupFlowPath() && isIncompleteSignupSession();

/**
 * SignupCompletionGuard: redirect mid-signup guests; allow authenticated
 * monitoring-tier customers with stale local signup flags.
 */
export const shouldRedirectIncompleteSignupToResume = () => {
  if (!isIncompleteSignupSession()) {
    return false;
  }

  if (!isAuthenticatedSession()) {
    return true;
  }

  // An authenticated session with no step left to resume is a finished signup
  // whose in-progress flag was never cleared. The signup route drops the token
  // it does not recognise, so bouncing there logs the user out.
  if (!hasMidSignupProgress()) {
    markSignupSessionComplete();
    return false;
  }

  const tier =
    localStorage.getItem("signup_product_tier") ||
    decryptPayload(localStorage.getItem("category"));
  if (tier === "basic_monitoring" || tier === "enhanced_monitoring") {
    markSignupSessionComplete();
    return false;
  }

  return true;
};

export const getSignupResumePath = () => {
  const countyCode = decryptPayload(localStorage.getItem("county-code"));
  if (localStorage.getItem("scrp_hp") && countyCode) {
    return `/${countyCode}/create-account`;
  }

  const encryptedPath = localStorage.getItem("path");
  const storedPath = encryptedPath ? decryptPayload(encryptedPath) : "";
  if (storedPath?.includes("create-account")) {
    return storedPath.split("?")[0] || "/create-account";
  }

  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const createAccountIndex = pathParts.indexOf("create-account");
  if (createAccountIndex >= 0) {
    return `/${pathParts.slice(0, createAccountIndex + 1).join("/")}`;
  }

  return "/create-account";
};

export const navigateToSignupResume = (navigate, options = {}) => {
  navigate(getSignupResumePath(), { replace: true, ...options });
};

export const getSavedSignupStep = () => {
  const step = parseInt(localStorage.getItem("currentStep"), 10);
  return Number.isNaN(step) ? 0 : step;
};

export const getSignupResumeStep = () => {
  if (isSignupSessionComplete()) {
    return undefined;
  }

  if (
    !localStorage.getItem("user_Id") ||
    localStorage.getItem("currentStep") === null
  ) {
    return undefined;
  }

  const step = parseInt(localStorage.getItem("currentStep"), 10);
  return Number.isNaN(step) ? undefined : step;
};

export const shouldShowSignupResumePrompt = (resumeCount) => {
  if (
    resumeCount === undefined ||
    resumeCount === null ||
    Number.isNaN(Number(resumeCount))
  ) {
    return false;
  }

  if (isSignupSessionComplete()) {
    return false;
  }

  return Boolean(
    localStorage.getItem("user_Id") &&
      localStorage.getItem("currentStep") !== null,
  );
};

export const getLennarSignupResumeStep = () => {
  if (
    !localStorage.getItem("user_Id") ||
    localStorage.getItem("currentStep") === null
  ) {
    return undefined;
  }

  const step = parseInt(localStorage.getItem("currentStep"), 10);
  return Number.isNaN(step) ? undefined : step;
};

export const shouldShowLennarSignupResumePrompt = (resumeCount) => {
  if (
    resumeCount === undefined ||
    resumeCount === null ||
    Number.isNaN(resumeCount)
  ) {
    return false;
  }

  return Boolean(
    localStorage.getItem("user_Id") &&
    localStorage.getItem("currentStep") !== null,
  );
};

export const getSignupUserId = () => {
  try {
    const rawUserId = localStorage.getItem("user_Id");
    if (rawUserId) {
      try {
        const parsedUserId = JSON.parse(rawUserId);
        if (
          parsedUserId !== null &&
          parsedUserId !== undefined &&
          parsedUserId !== ""
        ) {
          return parsedUserId;
        }
      } catch {
        const numericUserId = Number(rawUserId);
        if (!Number.isNaN(numericUserId) && numericUserId > 0) {
          return numericUserId;
        }
      }
    }
  } catch {
    // ignore parse errors
  }

  const signupDetails = user_signup_details();
  if (signupDetails?.user_id) {
    return signupDetails.user_id;
  }

  try {
    const sessionDetails = JSON.parse(
      sessionStorage.getItem("userSignupDetails") || "null",
    );
    if (sessionDetails?.data?.user_id) {
      return sessionDetails.data.user_id;
    }
  } catch {
    // ignore sessionStorage parse errors
  }

  return null;
};

const BUSINESS_OWNER_KEYWORDS = [
  "LLC",
  "INC",
  "LTD",
  "CORP",
  "CO",
  "COMPANY",
  "INVESTMENTS",
  "TRUST",
];

export const isBusinessEntityOwnerName = (name) => {
  if (!name || typeof name !== "string") {
    return false;
  }

  const upper = name.toUpperCase();
  return BUSINESS_OWNER_KEYWORDS.some((keyword) => upper.includes(keyword));
};

/** True when a value looks like a street address rather than a person's name. */
export const looksLikeStreetAddress = (value) => {
  if (!value || typeof value !== "string") {
    return false;
  }

  return /^\d+\s|\b(st|street|rd|road|ave|avenue|blvd|dr|drive|ln|lane|way|ct|court|pl|place|hwy|pkwy)\b/i.test(
    value.trim(),
  );
};

export const sanitizeBillingNameValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || looksLikeStreetAddress(trimmed)) {
    return "";
  }

  return trimmed;
};

export const sanitizeBillingNameFields = ({
  first_name = "",
  last_name = "",
} = {}) => ({
  first_name: sanitizeBillingNameValue(first_name),
  last_name: sanitizeBillingNameValue(last_name),
});

export const parsePersonalOwnerName = (fullName) => {
  if (!fullName || typeof fullName !== "string") {
    return null;
  }

  const trimmed = fullName.trim();
  if (!trimmed || isBusinessEntityOwnerName(trimmed)) {
    return null;
  }

  if (trimmed.includes(",")) {
    const [lastPart, firstPart] = trimmed
      .split(",", 2)
      .map((part) => part.trim());
    if (firstPart && lastPart && !isBusinessEntityOwnerName(lastPart)) {
      const firstParts = firstPart.split(/\s+/).filter(Boolean);
      if (firstParts.length > 0) {
        const middle = firstParts.slice(1).join(" ");
        return {
          first_name: firstParts[0],
          last_name: `${lastPart}${middle ? ` ${middle}` : ""}`.trim(),
        };
      }
    }
  }

  const cleaned = trimmed.replace(/,/g, "");
  if (!cleaned || isBusinessEntityOwnerName(cleaned)) {
    return null;
  }

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return null;
  }

  if (parts.length === 1) {
    return { first_name: parts[0], last_name: "" };
  }

  const suffixPattern = /^(jr\.?|sr\.?|[ivx]+|esq\.?)$/i;
  let suffix = "";
  if (suffixPattern.test(parts[parts.length - 1]) && parts.length > 2) {
    suffix = ` ${parts.pop()}`;
  }

  return {
    first_name: parts[0],
    last_name: `${parts.slice(1).join(" ")}${suffix}`.trim(),
  };
};

const extractEpAwareIdentityFields = (report) => {
  if (!report || typeof report !== "object") {
    return {};
  }

  const direct =
    report.identity_fields && typeof report.identity_fields === "object"
      ? report.identity_fields
      : {};
  const resolved = report.verification_result?.resolved_identity_fields;
  const resolvedFields =
    resolved && typeof resolved === "object" ? resolved : {};

  return { ...direct, ...resolvedFields };
};

export const buildBillingPrefillFromEpAwareReport = (report) => {
  const identity = extractEpAwareIdentityFields(report);
  const first_name = String(identity.first_name || "").trim();
  const last_name = String(identity.last_name || "").trim();
  const address = String(
    identity.address_line || identity.address || "",
  ).trim();
  const city = String(identity.city || "").trim();
  const state = String(identity.state || "").trim();
  const zip = String(identity.zip || "")
    .replace(/\D/g, "")
    .slice(0, 5);

  if (!first_name && !last_name && !address && !city) {
    return null;
  }

  return {
    first_name,
    last_name,
    address,
    city,
    state,
    zip,
    source: "id_verification",
  };
};

export const buildBillingPrefillFromIdMailingAddress = (mailing) => {
  if (!mailing || typeof mailing !== "object") {
    return null;
  }

  const address = String(mailing.address_line || mailing.address || "").trim();
  const city = String(mailing.city || "").trim();
  const state = String(mailing.state || "").trim();
  const zip = String(mailing.zip || "")
    .replace(/\D/g, "")
    .slice(0, 5);

  if (!address && !city && !state && !zip) {
    return null;
  }

  return { address, city, state, zip };
};

export const mergeSignupBillingPrefill = (base, patch) => {
  const merged = { ...(base || {}) };

  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      const isNameField = key === "first_name" || key === "last_name";
      if (
        isNameField &&
        patch?.source === "property_owner" &&
        (merged.source === "account" || merged.source === "id_verification") &&
        String(merged[key] || "").trim() !== ""
      ) {
        return;
      }
      merged[key] = value;
    }
  });

  if (patch?.source) {
    const hasName =
      String(merged.first_name || "").trim() !== "" ||
      String(merged.last_name || "").trim() !== "";
    if (hasName || patch.source === "property_owner") {
      merged.source = patch.source;
    }
  }

  return merged;
};

export const enrichPropertySearchDataForBillingLookup = (input = {}) => {
  const selectedProperty = input.selectedProperty || {};
  const propertyData = input.propertyData || input.propertyQuestion || {};
  let parsedTtb =
    input.ttbDetails || input.ttb_details || propertyData?.ttb_details;

  if (typeof parsedTtb === "string") {
    try {
      parsedTtb = JSON.parse(parsedTtb || "{}");
    } catch {
      parsedTtb = {};
    }
  }

  const propertyDetailsJson = parsePropertyJsonResponse(
    input.propertyDetails ??
      propertyData?.property_details ??
      selectedProperty?.property_details,
  );
  const selectedJson = parsePropertyJsonResponse(
    selectedProperty?.json_response,
  );
  const jsonResponse = parsePropertyJsonResponse(
    selectedJson?.sa_property_id || selectedJson?.sa_owner_1_first
      ? selectedJson
      : (propertyDetailsJson?.json_response ??
          propertyDetailsJson ??
          (propertyData?.sa_property_id || propertyData?.sa_owner_1_first
            ? propertyData
            : propertyData?.json_response)),
  );
  const ownerInfo =
    parsedTtb?.OwnerInfo ||
    propertyData?.OwnerInfo ||
    selectedProperty?.OwnerInfo ||
    jsonResponse?.OwnerInfo ||
    propertyDetailsJson?.OwnerInfo ||
    {};

  return {
    selectedProperty,
    propertyData,
    parsedTtb,
    propertyDetailsJson,
    jsonResponse,
    ownerInfo,
    ownerLookup: {
      ...jsonResponse,
      ...propertyDetailsJson,
      ...parsedTtb,
      ...propertyData,
      ...selectedProperty,
      OwnerInfo: ownerInfo,
      user_data: {
        ...(propertyData?.user_data || {}),
        ...(selectedProperty?.user_data || {}),
        ...(parsedTtb?.user_data || {}),
      },
      name: propertyData?.name || selectedProperty?.name,
    },
    siteAddress: buildPropertySiteAddress(
      getObjLength(selectedProperty)
        ? selectedProperty
        : {
            ...(propertyData?.param || {}),
            street_number: propertyData?.param?.street_number,
            site_route: propertyData?.param?.site_route,
          },
      jsonResponse,
    ),
  };
};

export const buildBillingPrefillFromPropertyRecord = (input = {}) => {
  const {
    selectedProperty,
    propertyData,
    ownerInfo,
    ownerLookup,
    siteAddress,
  } = enrichPropertySearchDataForBillingLookup(input);

  const mailCityState = ownerInfo?.["Mailing City & State"] || "";
  const cityStateParts = mailCityState.split(",").map((part) => part.trim());
  const address = String(
    ownerInfo?.["Mailing Address"] ||
      siteAddress ||
      propertyData?.address_1 ||
      "",
  ).trim();
  const city = String(
    cityStateParts[0] || propertyData?.city || selectedProperty?.city || "",
  ).trim();
  const state = String(
    cityStateParts[1] || propertyData?.state || selectedProperty?.state || "",
  ).trim();
  const zip = String(
    ownerInfo?.["Mailing Zip"] ||
      propertyData?.zip ||
      selectedProperty?.zip ||
      "",
  )
    .replace(/\D/g, "")
    .slice(0, 5);
  const ownerNames = resolvePropertyOwnerNameFields(ownerLookup);

  if (
    !address &&
    !city &&
    !state &&
    !zip &&
    !ownerNames.first_name &&
    !ownerNames.last_name
  ) {
    return null;
  }

  return {
    first_name: ownerNames.first_name,
    last_name: ownerNames.last_name,
    address,
    city,
    state,
    zip,
    source: "property_owner",
  };
};

export const persistPropertySearchAndBillingPrefillFromReport = (
  reportRecord = {},
  extras = {},
) => {
  if (!reportRecord || typeof reportRecord !== "object") {
    return null;
  }

  let parsedTtb = reportRecord.ttb_details || {};
  if (typeof parsedTtb === "string") {
    try {
      parsedTtb = JSON.parse(parsedTtb || "{}");
    } catch {
      parsedTtb = {};
    }
  }

  const propertyDetailsJson = parsePropertyJsonResponse(
    reportRecord.property_details,
  );
  const propertySearchData = propertyRiskReportDataObj(reportRecord);
  const merged = mergePropertySearchDataForStorage(
    propertySearchData,
    parsedTtb,
    {
      risk_report_id: reportRecord.id || reportRecord.risk_report_id,
      ...extras,
    },
    propertyDetailsJson,
  );

  localStorage.setItem("property_search_data", JSON.stringify(merged));

  const billingPrefill = buildBillingPrefillFromPropertyRecord({
    selectedProperty: extras.selectedProperty,
    propertyQuestion: reportRecord,
    propertyData: reportRecord,
    ttbDetails: parsedTtb,
    propertyDetails: propertyDetailsJson,
  });
  if (billingPrefill) {
    persistSignupBillingPrefill(billingPrefill);
  }

  return merged;
};

export const persistSignupAccountBillingNames = (details = {}) => {
  const signupDetails =
    details.user_signup_details &&
    typeof details.user_signup_details === "object"
      ? details.user_signup_details
      : {};
  const first_name = String(
    details.first_name || signupDetails.first_name || "",
  ).trim();
  const last_name = String(
    details.last_name || signupDetails.last_name || "",
  ).trim();

  if (!first_name && !last_name) {
    return;
  }

  const signupPayload = {
    ...signupDetails,
    first_name: first_name || signupDetails.first_name || "",
    last_name: last_name || signupDetails.last_name || "",
    email: details.email || signupDetails.email,
    phone: details.phone || signupDetails.phone,
    user_id: details.user_id || details.id || signupDetails.user_id,
  };

  if (signupPayload.user_id || signupPayload.email) {
    localStorage.setItem("userSignupDetails", JSON.stringify(signupPayload));
  }

  persistSignupBillingPrefill({
    first_name,
    last_name,
    source: "account",
  });
};

export const persistSignupBillingPrefill = (prefill) => {
  if (!prefill || typeof prefill !== "object") {
    return;
  }

  const existing = getSignupBillingPrefill();
  const merged = mergeSignupBillingPrefill(existing, prefill);
  if (Object.keys(merged).length === 0) {
    return;
  }

  localStorage.setItem("signupBillingPrefill", JSON.stringify(merged));
};

export const getSignupBillingPrefill = () => {
  try {
    const raw = localStorage.getItem("signupBillingPrefill");
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const clearSignupBillingPrefill = () => {
  localStorage.removeItem("signupBillingPrefill");
};

export const persistSignupVerificationMethod = (method) => {
  if (method === "id" || method === "manual") {
    localStorage.setItem("signupVerificationMethod", method);
  }
};

export const persistSignupVerificationOutcome = (data) => {
  if (!data || typeof data !== "object") {
    return;
  }

  if (data.owner_verified !== undefined) {
    localStorage.setItem(
      "signupOwnerVerified",
      data.owner_verified ? "1" : "0",
    );
  }

  if (data.manual_review_required !== undefined) {
    localStorage.setItem(
      "signupManualReviewRequired",
      data.manual_review_required ? "1" : "0",
    );
  }
};

export const shouldCompleteSignupAfterIdVerification = () => {
  if (localStorage.getItem("signupVerificationMethod") !== "id") {
    return false;
  }

  const ownerVerified =
    localStorage.getItem("signupOwnerVerified") === "1" ||
    localStorage.getItem("isOwnerVerified") === "1";
  const manualReviewRequired =
    localStorage.getItem("signupManualReviewRequired") === "1";

  return ownerVerified && !manualReviewRequired;
};

export const getSignupCompletionRoute = () => {
  if (shouldCompleteSignupAfterIdVerification()) {
    return "/user-profile";
  }
  // Page-scoped complimentary flag set during signup; avoid importing
  // affiliateSignup here (circular with encryptPayload).
  const complimentaryFlag = localStorage.getItem(
    "complimentary_monitoring_activated",
  );
  // County-site signups: El Dorado, Gaston, or the free site stored from
  // county/details (eldoradoSignup COUNTY_FREE_SITE_KEY).
  const fromWhere = localStorage.getItem("from_where");
  let countyFreeSite = null;
  try {
    countyFreeSite = JSON.parse(
      localStorage.getItem("county_free_site") || "null",
    );
  } catch {
    countyFreeSite = null;
  }
  const isComplimentary =
    complimentaryFlag === "1" ||
    complimentaryFlag === "true" ||
    ["eldorado_site", "gaston_site"].includes(fromWhere) ||
    Boolean(fromWhere && countyFreeSite?.fromWhere === fromWhere) ||
    localStorage.getItem("is_affiliate_user") === "1" ||
    localStorage.getItem("is_affiliate_user") === "true" ||
    Boolean(localStorage.getItem("county_user_id"));
  return isComplimentary
    ? "/property-monitoring-activated?complimentary=true"
    : "/property-monitoring-activated";
};

export const clearSignupVerificationState = () => {
  localStorage.removeItem("signupVerificationMethod");
  localStorage.removeItem("signupOwnerVerified");
  localStorage.removeItem("signupManualReviewRequired");
};

/** Keys the add-client / create-user wizard writes while it runs. */
export const ADD_CLIENT_WIZARD_STORAGE_KEYS = [
  "param",
  "fixParams",
  "formParams",
  "searched_Add",
  "searched_Add_popup",
  "county_data",
  "promoCode",
  "referralCode",
  "category",
  "isOwnerVerified",
  "OwnerVerifiedScreen",
  "subscriptionScreen",
  "planDetails",
  "Plan_Data",
  "Plan_Name",
  "add_subscription_category",
  "property_search_data",
  "button",
];

/** Keys that let a half-finished signup resume in this browser. */
export const SIGNUP_PROGRESS_STORAGE_KEYS = [
  "user_Id",
  "currentStep",
  "isResumeSignup",
  "completed",
  "userSignupDetails",
  "errorCount",
  "skip",
  "setLoginUserToken",
  "signup_product_tier",
  "plain_signup",
  "survey_email",
];

/**
 * Everything an agent's add-client flow keeps in the browser (wizard, signup
 * progress, CAR enrollment). Cleared when the agent's session ends so a later
 * login never picks up a stale half-added client. The client itself is saved
 * on the server and is resumed from the Clients list.
 */
export const clearServicerClientSignupState = () => {
  [
    ...ADD_CLIENT_WIZARD_STORAGE_KEYS,
    ...SIGNUP_PROGRESS_STORAGE_KEYS,
    "car_portfolio_id",
    "car_portfolio_ids",
    "carEnrollment",
    "carPortfolioPrefill",
  ].forEach((key) => localStorage.removeItem(key));
  try {
    sessionStorage.removeItem("userSignupDetails");
    sessionStorage.removeItem("addPropertyMailingNoMatch");
    sessionStorage.removeItem(CAR_SKIP_OWNER_PIN);
  } catch {
    // ignore storage failures
  }
  clearSignupInProgressFlag();
  clearSignupVerificationState();
};

const pushTitleOwnerName = (owners, json, ownerNumber) => {
  const ownerInfo = json?.OwnerInfo || json?.ownerInfo || json?.owner_info;
  const ownerInfoKey = ownerNumber === "2" ? "Owner Name 2" : "Owner Name";
  const ownerInfoName = String(ownerInfo?.[ownerInfoKey] || "").trim();
  if (ownerInfoName) {
    owners.push(ownerInfoName);
    return;
  }

  const formatted = String(
    json?.[`formatted_sa_owner_${ownerNumber}`] || "",
  ).trim();
  if (formatted) {
    owners.push(formatted);
    return;
  }

  const first = String(json?.[`sa_owner_${ownerNumber}_first`] || "").trim();
  const last = String(json?.[`sa_owner_${ownerNumber}_last`] || "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) {
    owners.push(full);
    return;
  }

  const combined = String(json?.[`sa_owner_${ownerNumber}`] || "").trim();
  if (combined.includes(" ")) {
    owners.push(combined);
    return;
  }

  if (ownerNumber === "1" && first) {
    owners.push(first);
  }
};

export const formatTitleOwnerNamesFromJson = (property = {}) => {
  const json = property?.json_response || {};
  const owners = [];

  pushTitleOwnerName(owners, json, "1");
  pushTitleOwnerName(owners, json, "2");

  const uniqueOwners = owners.filter(
    (name, index) => owners.indexOf(name) === index,
  );

  if (uniqueOwners.length > 0) {
    return uniqueOwners.join(" & ");
  }

  return String(property?.owner_name || property?.owner || "").trim();
};

/**
 * A servicer's Add Property popup quotes and charges only the property added in
 * it, never a property list another flow left staged (which once charged an
 * unpaid property from an abandoned Add Client instead).
 */
export const isServicerAddPropertyCheckout = ({
  isServiceProvider = false,
  from = "",
  selectedProperty = null,
} = {}) =>
  Boolean(isServiceProvider) &&
  from === "add-property" &&
  Boolean(selectedProperty?.property_id);

export const buildSignupPropertiesIdPayload = (propertyRecords = []) => {
  const records = (propertyRecords || []).filter(
    (record) => record?.property_id,
  );
  const propertyIds = records.map((record) => record.property_id);
  const ids = records
    .map((record) => record?.risk_report_id ?? record?.report_id ?? record?.id)
    .filter(Boolean);
  const properties = records.map((record) => ({
    address: record.address || record.display_address || "",
    address2: record.address2 || "",
    city: record.city || "",
    country: record.country || "US",
    latitude: record.latitude ?? null,
    longitude: record.longitude ?? null,
    property_id: record.property_id,
    site_route: record.site_route || "",
    state: record.state || "",
    street_number: record.street_number || "",
    zip: record.zip || "",
  }));

  return {
    propertyIds,
    properties,
    ids,
  };
};

/**
 * Owner Verification row that is a Basic/Enhanced risk report, not a CP property.
 * A risk row awaiting its CP payment stands for an unpaid CP property (upgrade).
 */
const isMonitoringRiskReportRow = (row) =>
  !row?.complete_protection_awaiting_payment &&
  (Boolean(row?.from_risk_measure) ||
    row?.product_tier === "enhanced_monitoring");

/**
 * Complete Protection parcel ids for get-final-amount / payment. Mixed county
 * investor signups also list Free Enhanced risk reports, whose parcels are not
 * properties rows and fail payment validation.
 */
export const resolveCompleteProtectionPropertyIds = (
  propertyIds = [],
  listedRows = [],
) => {
  const rows = Array.isArray(listedRows) ? listedRows : [];
  const toParcelSet = (filteredRows) =>
    new Set(filteredRows.map((row) => String(row?.property_id)));
  const riskParcels = toParcelSet(rows.filter(isMonitoringRiskReportRow));
  const cpParcels = toParcelSet(
    rows.filter((row) => !row?.not_added && !isMonitoringRiskReportRow(row)),
  );
  return (propertyIds || []).filter(
    (id) => cpParcels.has(String(id)) || !riskParcels.has(String(id)),
  );
};

/** Free Enhanced risk report ids listed alongside a Complete Protection signup. */
export const getListedEnhancedRiskReportIds = (listedRows = []) =>
  (Array.isArray(listedRows) ? listedRows : [])
    .filter(
      (row) =>
        !row?.not_added &&
        isMonitoringRiskReportRow(row) &&
        row?.from_risk_measure &&
        row?.product_tier === "enhanced_monitoring",
    )
    .map((row) => Number(row?.id))
    .filter((id) => Number.isFinite(id) && id > 0);

/** Risk report ids (or parcel ids as fallback) for enhanced monitoring payment. */
export const resolveEnhancedMonitoringReportIds = ({
  property_ids: riskReportIds = [],
  propertyIds = [],
} = {}) => {
  const resolved = new Set();

  (riskReportIds || [])
    .filter(Boolean)
    .forEach((id) => resolved.add(String(id)));

  if (resolved.size === 0) {
    (propertyIds || [])
      .filter(Boolean)
      .forEach((id) => resolved.add(String(id)));
  }

  if (resolved.size > 0) {
    return Array.from(resolved);
  }

  try {
    const raw = localStorage.getItem("property_search_data");
    if (raw) {
      const data = JSON.parse(raw);
      const riskReportId =
        data?.risk_report_id || data?.user_data?.risk_report_id || data?.id;
      if (riskReportId) {
        resolved.add(String(riskReportId));
      }
      if (resolved.size === 0) {
        const propertyId = data?.property_id || data?.user_data?.property_id;
        if (propertyId) {
          resolved.add(String(propertyId));
        }
      }
    }
  } catch {
    // ignore malformed localStorage
  }

  if (resolved.size === 0) {
    try {
      const storedReportId = localStorage.getItem("signup_risk_report_id");
      if (storedReportId) {
        resolved.add(String(storedReportId));
      }
    } catch {
      // ignore malformed localStorage
    }
  }

  if (resolved.size === 0) {
    try {
      const userSignupDetails = JSON.parse(
        localStorage.getItem("userSignupDetails") || "{}",
      );
      if (userSignupDetails?.risk_report_id) {
        resolved.add(String(userSignupDetails.risk_report_id));
      }
    } catch {
      // ignore malformed localStorage
    }
  }

  return Array.from(resolved);
};

export const persistSignupRiskReportId = (record) => {
  const reportId =
    record?.risk_report_id ?? record?.report_id ?? record?.id ?? null;
  if (!reportId) {
    return;
  }
  try {
    localStorage.setItem("signup_risk_report_id", String(reportId));
  } catch {
    // ignore storage failures
  }
};

export const persistSignupMonitoringPropertyIds = (
  dispatch,
  userSigupAction,
  record,
) => {
  if (!record?.property_id || !dispatch || !userSigupAction) {
    return;
  }
  persistSignupRiskReportId(record);
  dispatch(
    userSigupAction.setPropertiesId(buildSignupPropertiesIdPayload([record])),
  );
};

export const buildEpAwareApplyPropertyPayload = (property) => {
  if (!property?.property_id) {
    return null;
  }

  let jsonResponse = property?.json_response ?? property?.json_res ?? null;
  if (typeof jsonResponse === "string") {
    try {
      jsonResponse = JSON.parse(jsonResponse);
    } catch {
      jsonResponse = null;
    }
  }

  if (!jsonResponse || typeof jsonResponse !== "object") {
    return null;
  }

  return {
    property_id: String(property.property_id),
    json_response: jsonResponse,
    address: property.address || property.display_address || "",
    city: property.city || "",
    state: property.state || "",
    zip: property.zip || "",
    county: property.county || null,
    street_number:
      property.street_number || jsonResponse?.sa_site_street_nbr || null,
    site_route: property.site_route || null,
    latitude: property.latitude ?? jsonResponse?.sa_y_coord ?? null,
    longitude: property.longitude ?? jsonResponse?.sa_x_coord ?? null,
    country: property.country || "US",
  };
};

export const formatEpAwareMailingAddressLabel = (mailing) => {
  if (!mailing?.address_line) {
    return "";
  }

  return [mailing.address_line, mailing.city, mailing.state, mailing.zip]
    .filter(Boolean)
    .join(", ");
};

export const getSignupAccountNameFields = (userSignupDetails = null) => {
  const pick = (...values) => {
    for (const value of values) {
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        return String(value).trim();
      }
    }

    return "";
  };

  let localDetails = userSignupDetails;
  if (localDetails === null || localDetails === undefined) {
    localDetails = user_signup_details();
  }

  let sessionDetails = null;
  try {
    const raw = sessionStorage.getItem("userSignupDetails");
    sessionDetails = raw ? JSON.parse(raw) : null;
  } catch {
    sessionDetails = null;
  }

  const localNested =
    localDetails && typeof localDetails === "object"
      ? localDetails.data || {}
      : {};
  const sessionNested =
    sessionDetails && typeof sessionDetails === "object"
      ? sessionDetails.data || {}
      : {};

  return {
    first_name: pick(
      localDetails?.first_name,
      localNested?.first_name,
      sessionDetails?.first_name,
      sessionNested?.first_name,
      localNested?.first_owner_first_name,
      sessionNested?.first_owner_first_name,
      localDetails?.first_owner_first_name,
    ),
    last_name: pick(
      localDetails?.last_name,
      localNested?.last_name,
      sessionDetails?.last_name,
      sessionNested?.last_name,
      localNested?.first_owner_last_name,
      sessionNested?.first_owner_last_name,
      localDetails?.first_owner_last_name,
    ),
  };
};

export const resolvePropertyOwnerNameFields = (propertySearchData = {}) => {
  if (!propertySearchData || typeof propertySearchData !== "object") {
    return { first_name: "", last_name: "" };
  }

  const pickName = (...pairs) => {
    for (const [first, last] of pairs) {
      const firstName = sanitizeBillingNameValue(first);
      const lastName = sanitizeBillingNameValue(last);
      if (firstName || lastName) {
        return { first_name: firstName, last_name: lastName };
      }
    }
    return null;
  };

  const jsonResponse = parsePropertyJsonResponse(
    propertySearchData?.json_response ?? propertySearchData?.property_details,
  );

  const userData = propertySearchData?.user_data || {};
  const fromUserData = pickName(
    [userData.first_owner_first_name, userData.first_owner_last_name],
    [userData.first_name, userData.last_name],
    [
      propertySearchData.first_owner_first_name,
      propertySearchData.first_owner_last_name,
    ],
  );
  if (fromUserData) {
    return fromUserData;
  }

  const fromReportName = sanitizeBillingNameFields(
    parsePersonalOwnerName(
      propertySearchData?.name || jsonResponse?.name || "",
    ) || {},
  );
  if (fromReportName.first_name) {
    return fromReportName;
  }

  const fromJsonResponse = pickName(
    [jsonResponse?.sa_owner_1_first, jsonResponse?.sa_owner_1_last],
    [propertySearchData?.sa_owner_1_first, propertySearchData?.sa_owner_1_last],
  );
  if (fromJsonResponse) {
    return fromJsonResponse;
  }

  const ownerInfo =
    propertySearchData?.OwnerInfo || jsonResponse?.OwnerInfo || {};
  for (const ownerKey of ["Owner Name", "Owner Name 2"]) {
    const fromOwnerInfo = sanitizeBillingNameFields(
      parsePersonalOwnerName(ownerInfo[ownerKey] || "") || {},
    );
    if (fromOwnerInfo.first_name) {
      return fromOwnerInfo;
    }
  }

  const fromFormatted = sanitizeBillingNameFields(
    parsePersonalOwnerName(
      propertySearchData?.formatted_sa_owner_1 ||
        jsonResponse?.formatted_sa_owner_1 ||
        "",
    ) || {},
  );
  if (fromFormatted.first_name) {
    return fromFormatted;
  }

  const saFirst = sanitizeBillingNameValue(
    propertySearchData?.sa_owner_1_first ||
      jsonResponse?.sa_owner_1_first ||
      "",
  );
  const saLast = sanitizeBillingNameValue(
    propertySearchData?.sa_owner_1_last || jsonResponse?.sa_owner_1_last || "",
  );
  if (saFirst || saLast) {
    return { first_name: saFirst, last_name: saLast };
  }

  const fromOwnerString = sanitizeBillingNameFields(
    parsePersonalOwnerName(
      propertySearchData?.owner_name ||
        propertySearchData?.owner ||
        jsonResponse?.owner_name ||
        "",
    ) || {},
  );
  if (fromOwnerString.first_name) {
    return fromOwnerString;
  }

  return { first_name: "", last_name: "" };
};

export const mergePropertySearchDataForStorage = (
  propertySearchData = {},
  ttbDetails = {},
  extras = {},
  propertyDetails = null,
) => {
  const { selectedProperty, ...restExtras } = extras || {};
  const enriched = enrichPropertySearchDataForBillingLookup({
    selectedProperty,
    propertyData: propertySearchData,
    ttbDetails,
    propertyDetails,
  });
  const { jsonResponse, ownerInfo, ownerLookup, siteAddress } = enriched;
  const ownerNames = resolvePropertyOwnerNameFields(ownerLookup);
  const param = {
    ...(propertySearchData?.param || {}),
    street_number:
      selectedProperty?.street_number ??
      propertySearchData?.param?.street_number,
    site_route:
      selectedProperty?.site_route ?? propertySearchData?.param?.site_route,
    city: selectedProperty?.city ?? propertySearchData?.param?.city,
    state: selectedProperty?.state ?? propertySearchData?.param?.state,
    zip: selectedProperty?.zip ?? propertySearchData?.param?.zip,
    county: selectedProperty?.county ?? propertySearchData?.param?.county,
  };
  const fixParams = {
    ...(propertySearchData?.fixParams || {}),
    address:
      siteAddress ||
      propertySearchData?.fixParams?.address ||
      buildPropertySiteAddress(selectedProperty, jsonResponse),
  };

  return {
    ...propertySearchData,
    ...ttbDetails,
    ...jsonResponse,
    ...restExtras,
    ...(getObjLength(selectedProperty) ? { selectedProperty } : {}),
    json_response: jsonResponse,
    param,
    fixParams,
    sa_owner_1_first:
      ownerNames.first_name ||
      jsonResponse?.sa_owner_1_first ||
      selectedProperty?.sa_owner_1_first,
    sa_owner_1_last:
      ownerNames.last_name ||
      jsonResponse?.sa_owner_1_last ||
      selectedProperty?.sa_owner_1_last,
    user_data: {
      ...(propertySearchData?.user_data || {}),
      ...(selectedProperty?.user_data || {}),
      ...(ttbDetails?.user_data || {}),
      ...(ownerNames.first_name || ownerNames.last_name
        ? {
            first_owner_first_name: ownerNames.first_name,
            first_owner_last_name: ownerNames.last_name,
          }
        : {}),
    },
    OwnerInfo: ownerInfo,
  };
};

export const resolveSignupBillingPrefillForCheckout = ({
  mailingData,
  mailingAddress,
  propertySearchData,
  userSignupDetails,
  stateList = [],
} = {}) => {
  const stored = getSignupBillingPrefill() || {};
  const enriched = enrichPropertySearchDataForBillingLookup({
    selectedProperty: propertySearchData?.selectedProperty,
    propertyData: propertySearchData,
    ttbDetails: propertySearchData,
    propertyDetails: propertySearchData?.json_response,
  });
  const { ownerInfo, ownerLookup, siteAddress } = enriched;
  const propertyOwnerNames = sanitizeBillingNameFields(
    resolvePropertyOwnerNameFields(ownerLookup),
  );
  const accountNames = sanitizeBillingNameFields(
    getSignupAccountNameFields(userSignupDetails),
  );
  const idVerifiedNames =
    stored.source === "id_verification"
      ? sanitizeBillingNameFields({
          first_name: stored.first_name,
          last_name: stored.last_name,
        })
      : { first_name: "", last_name: "" };

  const pick = (primary, ...fallbacks) => {
    for (const value of [primary, ...fallbacks]) {
      const sanitized = sanitizeBillingNameValue(value);
      if (sanitized) {
        return sanitized;
      }
    }

    return "";
  };

  const preferAccountNames =
    stored.source === "account" ||
    stored.source === "id_verification" ||
    Boolean(accountNames.first_name || accountNames.last_name);

  const first_name = pick(
    idVerifiedNames.first_name,
    preferAccountNames
      ? accountNames.first_name
      : propertyOwnerNames.first_name,
    preferAccountNames ? stored.first_name : "",
    preferAccountNames ? "" : propertyOwnerNames.first_name,
    preferAccountNames
      ? propertyOwnerNames.first_name
      : accountNames.first_name,
    stored.first_name,
    mailingData?.first_name,
  );
  const last_name = pick(
    idVerifiedNames.last_name,
    preferAccountNames ? accountNames.last_name : propertyOwnerNames.last_name,
    preferAccountNames ? stored.last_name : "",
    preferAccountNames ? "" : propertyOwnerNames.last_name,
    preferAccountNames ? propertyOwnerNames.last_name : accountNames.last_name,
    stored.last_name,
    mailingData?.last_name,
  );
  const address = pick(
    siteAddress,
    ownerInfo?.["Mailing Address"],
    stored.address,
    mailingAddress?.address,
    propertySearchData?.fixParams?.address,
  );
  const city = pick(
    stored.city,
    mailingAddress?.city,
    ownerInfo?.["Mailing City & State"]?.split(",")?.[0]?.trim(),
    propertySearchData?.param?.city,
    propertySearchData?.selectedProperty?.city,
  );
  const state = normalizeUsStateValue(
    pick(
      stored.state,
      mailingAddress?.state,
      ownerInfo?.["Mailing City & State"]?.split(",")?.[1]?.trim(),
      propertySearchData?.param?.state,
      propertySearchData?.selectedProperty?.state,
    ),
    stateList,
  );
  const zip = pick(
    stored.zip,
    mailingAddress?.zip,
    ownerInfo?.["Mailing Zip"],
    propertySearchData?.param?.zip,
    propertySearchData?.selectedProperty?.zip,
  )
    .replace(/\D/g, "")
    .slice(0, 5);

  return { first_name, last_name, address, city, state, zip };
};

export const resolveCheckoutPropertySearchData = (propertySearchData = {}) => {
  const stored =
    propertySearchData && typeof propertySearchData === "object"
      ? propertySearchData
      : parseLocalStorageJson("property_search_data") || {};

  const { ownerLookup } = enrichPropertySearchDataForBillingLookup({
    selectedProperty: stored?.selectedProperty,
    propertyData: stored,
    ttbDetails: stored,
    propertyDetails: stored?.json_response,
  });
  const ownerNames = resolvePropertyOwnerNameFields(ownerLookup);
  if (ownerNames.first_name || ownerNames.last_name) {
    return {
      ...stored,
      ...ownerLookup,
      sa_owner_1_first: ownerNames.first_name || stored?.sa_owner_1_first,
      sa_owner_1_last: ownerNames.last_name || stored?.sa_owner_1_last,
      user_data: {
        ...(stored.user_data || {}),
        ...(ownerNames.first_name || ownerNames.last_name
          ? {
              first_owner_first_name: ownerNames.first_name,
              first_owner_last_name: ownerNames.last_name,
            }
          : {}),
      },
    };
  }

  const billingPrefill = getSignupBillingPrefill() || {};
  if (!billingPrefill.first_name && !billingPrefill.last_name) {
    return stored;
  }

  return {
    ...stored,
    sa_owner_1_first: billingPrefill.first_name,
    sa_owner_1_last: billingPrefill.last_name,
    user_data: {
      ...(stored.user_data || {}),
      first_owner_first_name: billingPrefill.first_name,
      first_owner_last_name: billingPrefill.last_name,
    },
  };
};

export const dynamicReadMoreText = (text, words) => (
  <>
    <span className="para_texts">
      {text.slice(0, words)}
      {text.length > words + 1 ? "..." : ""}
    </span>
  </>
);

// Fire React Pixel events
export const reactPixelEvent = (pageView, event) => {
  if (SERVER === "production") {
    console.log(pageView, "=called==event==>", event);
    const fireEvents = () => {
      ReactPixel.pageView();
      ReactPixel.track(pageView, { event_param: event });
      if (window && window.gtag) {
        window.gtag("event", pageView, event);
      }
    };
    return fireEvents();
  }
};

export const reactPixelEventOnlyForCustomer = (pageView, event) => {
  let userRole = decryptPayload(localStorage.getItem("roles"));
  console.log("Role==>", userRole);
  // if (SERVER === "production") {
  if (userRole?.includes(ROLES.customer)) {
    console.log(pageView, "=called==event==> users", event);
    const fireEvents = () => {
      ReactPixel.pageView();
      ReactPixel.track(pageView, { event_param: event });
      // window.gtag("event", pageView, event);
    };
    return fireEvents();
  }
};

export const reactPixelEventOnConversion = (pageView, event) => {
  let storedId = `${Date.now()}-${Math.floor(Math.random() * 1000000)}`; // Generate unique ID;

  if (SERVER === "production") {
    const img = document.createElement("img");
    img.src = `https://trkn.us/pixel/c?ppt=25200&g=sitewide&gid=63320&ord=[${storedId}]`;
    img.height = "0";
    img.width = "0";
    img.border = "0";
    img.style.display = "none";
    img.id = "trackingPixel"; // Assign an ID for reference

    // Append to the body
    return document.body.appendChild(img);
  }
};

// get professional type label
export const getProfessionalTypeLabel = (professionalType) => {
  return professionalType === "lender"
    ? PROFESSIONAL_TYPE?.lender
    : professionalType === "broker"
      ? PROFESSIONAL_TYPE?.broker
      : professionalType === "provider"
        ? PROFESSIONAL_TYPE?.provider
        : PROFESSIONAL_TYPE?.other;
};

// capitalize the first character of the word in string
export const capitalizeFirstCharInWord = (str = "") => {
  return str
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};

// generate BG-color and display name on the profile picture
const stringToColor = (string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};
export const stringAvatar = (name) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
  };
};
// to get 1st, 2nd , 3rd, 4th type text from number
export const ordinalSuffixOf = (i) => {
  var j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
};

let suppressSignupLeaveWarning = false;

export const disableLeavePageWarning = () => {
  suppressSignupLeaveWarning = true;
  window.onbeforeunload = null;
};

export const registerSignupLeavePageWarning = () => {
  suppressSignupLeaveWarning = false;
  window.onbeforeunload = () => {
    if (suppressSignupLeaveWarning) {
      return undefined;
    }
    return true;
  };
};

const PORTAL_SESSION_TRANSFER_KEY = "portalSession";
const PORTAL_SESSION_TRANSFER_TTL_MS = 5 * 60 * 1000;

// A login that started on the CAR site and moved to the servicer site goes back
// to the CAR site when it ends (sign out, expired or archived session).
// Every login sets or clears this; logging out keeps it.
const LOGIN_PORTAL_KEY = "loginPortal";

export const rememberLoginPortal = (hostname = window.location.hostname) => {
  if (isCarHost(hostname)) {
    localStorage.setItem(LOGIN_PORTAL_KEY, "car");
  } else {
    localStorage.removeItem(LOGIN_PORTAL_KEY);
  }
};

/** CAR agents (signup_source "car") belong to the CAR site wherever they logged in. */
export const isCarPortalLogin = () =>
  localStorage.getItem(LOGIN_PORTAL_KEY) === "car" ||
  localStorage.getItem("signup_source") === "car";

/**
 * CAR login page for a CAR session ending on the servicer site, or null.
 * `session` ("expired" / "archived") lets the CAR login page say why.
 */
export const carLoginUrlForServicerExit = (
  session = "",
  hostname = window.location.hostname,
) => {
  if (!isServiceProviderPortalHost(hostname) || !isCarPortalLogin()) {
    return null;
  }

  return `${getCarUrl()}login${session ? `?session=${session}` : ""}`;
};

/** Login page a servicer-site exit should open (CAR login for CAR sessions). */
export const servicerExitLoginUrl = () =>
  carLoginUrlForServicerExit() || `${getServiceProvidersUrl()}login`;

const capturePortalSessionForTransfer = () => ({
  loginPortal: isCarHost() ? "car" : localStorage.getItem(LOGIN_PORTAL_KEY),
  authToken: localStorage.getItem("authToken"),
  authorised: localStorage.getItem("authorised"),
  roles: localStorage.getItem("roles"),
  activeRole: localStorage.getItem("activeRole"),
  userRoles: localStorage.getItem("userRoles"),
  defaultPortal: localStorage.getItem("defaultPortal"),
  uID: localStorage.getItem("uID"),
  ts: Date.now(),
});

const encryptPortalSessionBlob = (sessionObject) => {
  const json = JSON.stringify(sessionObject);
  const encrypted = CryptoJS.AES.encrypt(json, secretKey, { iv });
  const ciphertext = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
  const hmac = CryptoJS.enc.Base64.stringify(
    CryptoJS.HmacSHA256(ciphertext, secretKey),
  );

  return ciphertext + hmac;
};

const decryptPortalSessionBlob = (encryptedPayload) => {
  if (!encryptedPayload || encryptedPayload.length <= 44) {
    return null;
  }

  const receivedCiphertext = encryptedPayload.substring(
    0,
    encryptedPayload.length - 44,
  );
  const hmacReceived = encryptedPayload.substring(encryptedPayload.length - 44);
  const hmacExpected = CryptoJS.enc.Base64.stringify(
    CryptoJS.HmacSHA256(receivedCiphertext, secretKey),
  );

  if (hmacReceived !== hmacExpected) {
    return null;
  }

  const decrypted = CryptoJS.AES.decrypt(receivedCiphertext, secretKey, { iv });
  const plaintext = decrypted.toString(CryptoJS.enc.Utf8);

  if (!plaintext) {
    return null;
  }

  try {
    return JSON.parse(plaintext);
  } catch {
    return null;
  }
};

export const appendPortalSessionTransferToUrl = (url) => {
  if (typeof window === "undefined" || !hasValidAuthToken()) {
    return url;
  }

  const session = capturePortalSessionForTransfer();
  if (!session.authToken) {
    return url;
  }

  const encrypted = encryptPortalSessionBlob(session);
  const hash = `${PORTAL_SESSION_TRANSFER_KEY}=${encodeURIComponent(encrypted)}`;
  const [base, existingHash = ""] = String(url).split("#");
  const nextHash = existingHash ? `${existingHash}&${hash}` : hash;

  return `${base}#${nextHash}`;
};

export const applyPortalSessionFromUrl = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const rawHash = window.location.hash.replace(/^#/, "");
  if (!rawHash.includes(`${PORTAL_SESSION_TRANSFER_KEY}=`)) {
    return false;
  }

  const params = new URLSearchParams(rawHash);
  const encrypted = params.get(PORTAL_SESSION_TRANSFER_KEY);
  const session = decryptPortalSessionBlob(
    encrypted ? decodeURIComponent(encrypted) : "",
  );

  if (
    !session?.authToken ||
    session.authToken === "null" ||
    session.authToken === "undefined"
  ) {
    return false;
  }

  if (
    !session.ts ||
    Date.now() - Number(session.ts) > PORTAL_SESSION_TRANSFER_TTL_MS
  ) {
    return false;
  }

  localStorage.setItem("authToken", session.authToken);
  localStorage.setItem("authorised", session.authorised || "true");

  if (session.roles) {
    localStorage.setItem("roles", session.roles);
  }
  if (session.activeRole) {
    localStorage.setItem("activeRole", session.activeRole);
  }
  if (session.userRoles) {
    localStorage.setItem("userRoles", session.userRoles);
  }
  if (session.defaultPortal) {
    localStorage.setItem("defaultPortal", session.defaultPortal);
  }
  if (session.uID) {
    localStorage.setItem("uID", session.uID);
  }
  if (session.loginPortal === "car") {
    localStorage.setItem(LOGIN_PORTAL_KEY, "car");
  } else {
    localStorage.removeItem(LOGIN_PORTAL_KEY);
  }

  markLoginSessionFresh();
  clearLoginInProgress();

  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}`,
  );

  return true;
};

/**
 * A service-provider login handed to the servicer site moves there: the site it
 * came from (main or CAR host) must not keep a copy, or a later logout on the
 * servicer site leaves it behind and My Account hands the dead login over again.
 * Other roles (e.g. a customer following a "For Service Providers" link) keep
 * their session, and nothing is cleared when already on the servicer site.
 */
export const shouldClearSessionAfterServicerHandoff = ({
  roleLabels = [],
  sourceOrigin,
  targetUrl,
}) => {
  let targetOrigin = "";
  try {
    targetOrigin = new URL(targetUrl).origin;
  } catch {
    return false;
  }
  return (
    targetOrigin !== sourceOrigin &&
    roleLabels.some((role) => isServiceProviderPortalRole(role))
  );
};

const storedSessionRoleLabels = () =>
  [
    localStorage.getItem("activeRole"),
    ...String(decryptPayload(localStorage.getItem("roles")) || "").split(","),
  ]
    .map((role) => String(role || "").trim())
    .filter(Boolean);

export const navigateToDomain = (route = "") => {
  const path = route ? String(route).replace(/^\//, "") : "";
  let nextUrl = `${getServiceProvidersUrl()}${path}`;
  if (typeof window !== "undefined") {
    const current = window.location.href
      .split("#")[0]
      .split("?")[0]
      .replace(/\/$/, "");
    const target = nextUrl.split("#")[0].split("?")[0].replace(/\/$/, "");
    if (current === target) {
      return;
    }
  }
  const transferUrl = appendPortalSessionTransferToUrl(nextUrl);
  if (
    transferUrl !== nextUrl &&
    shouldClearSessionAfterServicerHandoff({
      roleLabels: storedSessionRoleLabels(),
      sourceOrigin: window.location.origin,
      targetUrl: nextUrl,
    })
  ) {
    clearStaleAuthSession(true);
    localStorage.removeItem("uID");
    localStorage.removeItem("defaultPortal");
  }
  window.location.href = transferUrl;
};

/** The EquityProtect (customer) site for this environment, with a trailing slash. */
export const getCustomerSiteUrl = () => {
  if (typeof window !== "undefined" && isLocalDevHost()) {
    return `${window.location.origin}/`;
  }
  if (SERVER === "production") {
    return "https://equityprotect.com/";
  }
  if (SERVER === "staging") {
    return getCustomerStagingUrl();
  }
  return "http://localhost:3000/";
};

export const navigateToCustomerDomain = (route = "") => {
  const path = route ? String(route).replace(/^\//, "") : "";
  window.location.href = `${getCustomerSiteUrl()}${path}`;
};

// Parse card exp_month (numeric, abbreviated, or full name) to 0-based month index.
export const parseCardExpMonthToIndex = (expMonth) => {
  if (expMonth == null || expMonth === "") {
    return -1;
  }

  const raw = String(expMonth).trim();
  const numeric = parseInt(raw, 10);

  if (!Number.isNaN(numeric) && numeric >= 1 && numeric <= 12) {
    return numeric - 1;
  }

  const normalized = raw.toLowerCase();
  const exactIndex = MONTHS.findIndex(
    (month) => month.toLowerCase() === normalized,
  );
  if (exactIndex !== -1) {
    return exactIndex;
  }

  const prefixIndex = MONTHS.findIndex((month) =>
    month.toLowerCase().startsWith(normalized),
  );
  if (prefixIndex !== -1) {
    return prefixIndex;
  }

  return MONTHS.findIndex((month) => month.toLowerCase().includes(normalized));
};

// get month from month name and year
export const getMonthFromString = (mon, year) => {
  const monthIndex = parseCardExpMonthToIndex(mon);
  if (monthIndex !== -1) {
    return monthIndex + 1;
  }

  const parsedMonth = new Date(Date.parse(`${mon} 1, ${year}`)).getMonth();
  return Number.isNaN(parsedMonth) ? 0 : parsedMonth + 1;
};

export const formatCardExpMonthForApi = (date) => {
  // Stripe exp_month is 1-12; Date.getMonth() is 0-11.
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const stripeMonth = date.getMonth() + 1;
  if (stripeMonth < 1 || stripeMonth > 12) {
    return "";
  }

  return stripeMonth;
};

// get local date and time for createdAt date
export const getLocalFormatedDate = (createdAtDate, time = true) => {
  const utcDate = new Date(createdAtDate);

  // const userTime = utcDate?.toLocaleString(undefined, {
  //   timeZone: userTimeZone,
  //   year: "numeric",
  //   month: "2-digit",
  //   day: "2-digit",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   second: "2-digit",
  //   hour12: true,
  // });

  // return userTime?.toUpperCase()?.split(",")?.join(" ") || "-";
  const localDate = new Date(
    utcDate.toLocaleString("en-US", { timeZone: userTimeZone }),
  );

  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");
  const year = localDate.getFullYear();

  const hours = localDate.getHours();
  const minutes = String(localDate.getMinutes()).padStart(2, "0");
  const seconds = String(localDate.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  const formattedHours = String(hours % 12 || 12).padStart(2, "0");

  const formattedDate = `${month}/${day}/${year}`;
  const formattedTime = `${formattedHours}:${minutes}:${seconds} ${ampm}`;

  return `${formattedDate} ${time ? formattedTime : ""}`;
};

export const getLocalFormattedDateParts = (createdAtDate) => {
  if (!createdAtDate) {
    return { date: "-", time: null };
  }

  const formatted = getLocalFormatedDate(createdAtDate, true).trim();
  if (!formatted || formatted === "-") {
    return { date: "-", time: null };
  }

  const parts = formatted.split(/\s+/);
  if (parts.length <= 1) {
    return { date: parts[0] || "-", time: null };
  }

  const date = parts[0];
  const time = parts.slice(1).join(" ");
  return { date, time };
};

// get local date and time for ExpiredAt date
export const getLocalFormatedDateForExpireDate = (expiredAt, timeString) => {
  const [day, month, year] = expiredAt.split("/").map(Number);
  // Create a new Date object
  const inputDate = new Date(year, month - 1, day);

  // Split the string into an array of strings
  const timeArray = timeString.split(", ");

  // Convert each string to a number
  const [hour, minutes, seconds] = timeArray.map(Number);

  // Set the time to 23:59:59
  inputDate.setHours(hour, minutes, seconds, 999);

  // Helper function to pad single-digit numbers with a leading zero
  function padZero(number) {
    return number.toString().padStart(2, "0");
  }

  // Helper function to format the time as "HH:mm:ss A"
  function formatTime(date) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const period = hours < 12 ? "AM" : "PM";

    return `${padZero(hours % 12)}:${padZero(minutes)}:${padZero(
      seconds,
    )} ${period}`;
  }
  // Format the date to "DD/MM/YYYY HH:mm:ss A"
  const formattedDateString = `${padZero(month)}/${padZero(
    day,
  )}/${year} ${formatTime(inputDate)}`;

  return formattedDateString;
};

export const calulateDatefromMonthsLater = (date, months) => {
  const inputDate = new Date(date); // i.e., date = 2023-09-29T12:04:34.000000Z
  const monthsLater = new Date(inputDate);
  monthsLater.setMonth(inputDate.getMonth() + months);

  const options = { year: "numeric", month: "long", day: "numeric" };
  const formattedDate = monthsLater.toLocaleDateString("en-US", options);

  return formattedDate; // Output date: "November 29, 2023"
};

export const calulateDatefromDaysLater = (date, days, type) => {
  const currentDate = new Date(date);
  const futureDate = new Date(
    currentDate.getTime() + days * 24 * 60 * 60 * 1000,
  );
  let afterOneMM = 0;
  let afterOneYY = 0;
  if (parseInt(days) === 0) {
    if (type === "MM") afterOneMM = 1;
    if (type === "YY") afterOneYY = 1;
  }

  const day = futureDate.getDate();
  const month = MONTHS[futureDate.getMonth() + afterOneMM];
  const year = futureDate.getFullYear() + afterOneYY;

  return `${month} ${day}, ${year}`;
};

export const loadScriptByURL = (id, url, callback = () => {}) => {
  const isScriptExist = document.getElementById(id);

  if (!isScriptExist) {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    script.id = id;
    script.defer = true;
    script.onload = callback;
    document.body.appendChild(script);
  }

  if (isScriptExist && callback) callback();
};

// get Prefix of the url based on role customer / professional
export const getPrefixUrl = (userProfileDetails = {}) => {
  if (
    userProfileDetails?.data?.user_type?.includes(ROLES?.professional) &&
    !userProfileDetails?.data?.role?.includes(ROLES?.customer)
  ) {
    return "professional";
  } else {
    return "customer";
  }
};

/** True when running inside the native iOS app WebView. */
export const isIosApp = () => {
  try {
    if (JSON.parse(localStorage.getItem("isIos")) === true) {
      return true;
    }
  } catch {
    // ignore invalid localStorage value
  }

  if (typeof window === "undefined") {
    return false;
  }

  if (window.__EP_NATIVE_SESSION_READY__) {
    return true;
  }

  if (
    window.webkit?.messageHandlers?.invokeNativeLogout ||
    window.EquityProtectNative ||
    window.ReactNativeWebView
  ) {
    return true;
  }

  try {
    const from = new URLSearchParams(window.location.search).get("from");
    if (from === "ios") {
      return true;
    }
  } catch {
    // ignore URL parse errors
  }

  return false;
};

/* Storing user's device details in a variable*/
export const isMobileDeviceCheck = (isMobile) => {
  let details = navigator.userAgent;

  /* Creating a regular expression 
containing some mobile devices keywords 
to search it in details string*/
  let regexp = /android|iphone|kindle|ipad/i;

  /* Using test() method to search regexp in details
it returns boolean value*/
  let isMobileDevice = regexp.test(details);

  if (isMobileDevice) {
    console.log("You are using a Mobile Device");
    return true;
  } else {
    console.log("You are using Desktop");
    return false;
  }
};

/**
 * Email returned for a login link's ?hint= token. The backend decrypts the
 * token; anything that is not a valid email address becomes "".
 */
export const normalizeLoginHintEmail = (value) => {
  const email = String(value ?? "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : "";
};

// Encryption function
export function encryptPayload(payload) {
  if (payload && payload !== "undefined") {
    const value = Array.isArray(payload) ? payload?.[0] : payload;
    const encryptedPayload = CryptoJS.AES.encrypt(value, secretKey, { iv: iv });
    const ciphertext = CryptoJS.enc.Base64.stringify(
      encryptedPayload.ciphertext,
    );

    // Generate HMAC
    const hmac = CryptoJS.HmacSHA256(ciphertext, secretKey);
    const hmacString = CryptoJS.enc.Base64.stringify(hmac);

    // Append HMAC to ciphertext
    const finalCiphertext = ciphertext + hmacString;
    return finalCiphertext;
  } else {
    return payload;
  }
}

// Decryption function
export function decryptPayload(encryptedPayload) {
  if (encryptedPayload && encryptedPayload !== "undefined") {
    const receivedCiphertext = encryptedPayload.substring(
      0,
      encryptedPayload.length - 44,
    ); // Assuming the HMAC is 44 characters long

    const hmacReceived = encryptedPayload.substring(
      encryptedPayload.length - 44,
    );
    const hmacExpected = CryptoJS.enc.Base64.stringify(
      CryptoJS.HmacSHA256(receivedCiphertext, secretKey),
    );

    if (hmacReceived !== hmacExpected) {
      // console.log("Invalid HMAC. Ciphertext may be tampered.");
      return null;
    } else {
      const decrypted = CryptoJS.AES.decrypt(receivedCiphertext, secretKey, {
        iv: iv,
      });
      const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
      return plaintext;
    }
  } else {
    return encryptedPayload;
  }
}
// encrypt password
function xorEncrypt(data, key) {
  // Ensure key is a string

  if (typeof key !== "string") {
    throw new Error("Key must be a string");
  }

  return data
    .split("")
    .map((char, index) => {
      return String.fromCharCode(
        char.charCodeAt(0) ^ key.charCodeAt(index % key.length),
      );
    })
    .join("");
}

function base64Encode(data) {
  return btoa(data); // btoa() encodes a string to Base64
}

export function encryptData(data, key) {
  const xorEncrypted = xorEncrypt(data, key);
  return base64Encode(xorEncrypted);
}

export const daysPassedSinceISODate = (isoDate) => {
  // Parse the ISO date string to a Date object
  const startDate = new Date(isoDate);
  // console.log("sentDate", startDate);

  // Get the current date
  const currentDate = new Date();
  // console.log("currentDate", currentDate);

  // Calculate the time difference in milliseconds
  const timeDifference = currentDate - startDate;
  // console.log(
  //   "timeDifference",
  //   timeDifference,
  //   "dayPassed",
  //   timeDifference / (1000 * 60 * 60 * 24)
  // );

  // Convert milliseconds to days
  const daysPassed = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  // console.log("dayPassed", daysPassed);

  return daysPassed;
};

// get data from other array based on subcription id
export function getDatafromArray(value, data, fetchID) {
  if (Array.isArray(value)) {
    return value.map((id) => {
      const item = data.find((item) => item.subscription?.id === id);
      return item?.address;
    });
  } else {
    const item = data.find((item) => item.subscription?.id === value);
    return item?.address;
  }
}
// get data from other array based on subcription id
export function filteroutPlanData(selectedData) {
  let recording_fees = selectedData?.plans?.[0]?.additional_charges
    ?.recording_fees
    ? selectedData?.plans?.[0]?.additional_charges?.recording_fees
    : null;

  let monthly_plans = {
    name: "Monthly",
    is_active: selectedData?.plans?.find((plan, i) => plan?.name === "Monthly")
      ? true
      : false,
    amount: selectedData?.plans?.find((plan, i) => plan?.name === "Monthly")
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Monthly" && plan?.is_default,
        )?.amount
      : null,
    setup_fees: selectedData?.plans?.find((plan, i) => plan?.name === "Monthly")
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Monthly" && plan?.is_default,
        )?.additional_charges?.["setup fees"]
      : null,
    trial_period: selectedData?.plans?.find(
      (plan, i) => plan?.name === "Monthly",
    )
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Monthly" && plan?.is_default,
        )?.trial_period
      : null,
    monthly_renewal_plans: selectedData?.plans?.find(
      (plan, i) => plan?.name === "Monthly",
    )
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Monthly" && !plan?.is_default,
        )?.amount
      : null,
  };

  let yearly_plans = {
    name: "Yearly",
    is_active: selectedData?.plans?.find((plan, i) => plan?.name === "Yearly")
      ? true
      : false,
    amount: selectedData?.plans?.find((plan, i) => plan?.name === "Yearly")
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Yearly" && plan?.is_default,
        )?.amount
      : null,
    trial_period: selectedData?.plans?.find(
      (plan, i) => plan?.name === "Yearly",
    )
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Yearly" && plan?.is_default,
        )?.trial_period
      : null,
    yearly_renewal_plans: selectedData?.plans?.find(
      (plan, i) => plan?.name === "Yearly",
    )
      ? selectedData?.plans?.find(
          (plan, i) => plan?.name === "Yearly" && !plan?.is_default,
        )?.amount
      : null,
  };

  let data = { recording_fees, monthly_plans, yearly_plans };
  return data;
}

// Set read more if string contains more than 80 letters
export const readMoreSentance = (str = "") => {
  return `${str.slice(0, 65)}${str.length > 65 ? `...` : ""}`;
};

export const isUnknownUserApiMessage = (message = "") => {
  const normalized = String(message || "")
    .trim()
    .toLowerCase();
  return normalized.includes("user does not exist");
};

/** Backend AWSAuth returns 403 + account_archived when a signed-in user is archived. */
export const isAccountArchivedApiResponse = (data) => {
  if (!data || typeof data !== "object") {
    return false;
  }

  if (data.account_archived === true || data.data?.account_archived === true) {
    return true;
  }

  const message = String(
    data.message || data.data?.message || "",
  ).toLowerCase();
  return (
    message.includes("closed and archived") ||
    message.includes("account has been closed and archived")
  );
};

/** True when a user record (or raw status string) is archived. */
export const isUserStatusArchived = (userOrStatus) => {
  const status =
    typeof userOrStatus === "string" || typeof userOrStatus === "number"
      ? userOrStatus
      : (userOrStatus?.status ??
        userOrStatus?.data?.status ??
        userOrStatus?.user?.status ??
        "");
  return String(status).trim().toLowerCase() === "archived";
};

export const shouldExpireSessionForApiStatus = (status, data) => {
  if (status === 401) {
    return true;
  }

  return status === 403 && isAccountArchivedApiResponse(data);
};

export const getAccountArchivedMessage = (data) => {
  const message = data?.message || data?.data?.message;
  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  return "This account has been closed and archived. Please contact support if you need assistance.";
};

const LOGIN_SESSION_GRACE_MS = 8000;
const IN_APP_NAVIGATION_GRACE_MS = 5000;

export const markLoginSessionFresh = () => {
  localStorage.setItem("loginSessionAt", String(Date.now()));
};

export const markLoginInProgress = () => {
  localStorage.setItem("loginInProgress", "true");
};

export const clearLoginInProgress = () => {
  localStorage.removeItem("loginInProgress");
};

export const shouldIgnoreUnauthorizedRedirect = () => {
  if (localStorage.getItem("loginInProgress") === "true") {
    return true;
  }

  const inAppNavigationAt = Number(
    sessionStorage.getItem("epInAppNavigationAt") || 0,
  );
  if (
    inAppNavigationAt > 0 &&
    Date.now() - inAppNavigationAt < IN_APP_NAVIGATION_GRACE_MS
  ) {
    return true;
  }

  const loginSessionAt = Number(localStorage.getItem("loginSessionAt") || 0);
  return (
    loginSessionAt > 0 && Date.now() - loginSessionAt < LOGIN_SESSION_GRACE_MS
  );
};

export const handleUnauthorizedApiResponse = (
  path = window.location.pathname,
) => {
  if (shouldIgnoreUnauthorizedRedirect()) {
    return false;
  }

  if (
    path !== "/login" &&
    !path?.includes("investors") &&
    !path?.toLowerCase()?.includes("bellland")
  ) {
    localStorage.setItem("previousPath", path);
    localStorage.setItem("previousUserID", localStorage.getItem("uID"));
  }

  localStorage.removeItem("authToken");
  localStorage.removeItem("authorised");
  localStorage.removeItem("agentauthToken");
  localStorage.removeItem("agent_role");
  localStorage.removeItem("agent_id");
  localStorage.removeItem("roles");
  localStorage.removeItem("activeRole");
  localStorage.removeItem("userRoles");

  // Agents logged out mid add-client must not come back to stale wizard data.
  if (isServiceProviderPortalHost()) {
    clearServicerClientSignupState();
  }

  if (
    path === "/login" ||
    path?.includes("/agent-login") ||
    path?.includes("investor-login")
  ) {
    return false;
  }

  return true;
};

const SESSION_EXPIRED_DEBOUNCE_MS = 5000;

/**
 * The shell ignores repeat handoffs once it is leaving, so re-posting is safe —
 * and a single dropped message strands the WebView on the handoff screen.
 */
const NATIVE_LOGOUT_RETRY_DELAYS_MS = [500, 1500];

/** Returns false when no native shell could be reached, so callers can fall back to web. */
export const invokeNativeAppLogout = (payload = {}) => {
  // While this bootstrap flag is set, every login-route recovery path stands
  // down — so a dropped handoff had nothing to fall back on. The native session
  // is over here, so let those guards run again.
  try {
    if (typeof window !== "undefined") {
      window.__EP_NATIVE_SESSION_READY__ = false;
    }
  } catch {
    // ignore
  }

  const delivered = postNativeBridgeMessage(
    "invokeNativeLogout",
    payload || {},
  );

  if (delivered) {
    NATIVE_LOGOUT_RETRY_DELAYS_MS.forEach((delay) => {
      window.setTimeout(
        () => postNativeBridgeMessage("invokeNativeLogout", payload || {}),
        delay,
      );
    });
  }

  windowsAndroidNativeMethods("logout");

  return delivered || isAndroidAppWebView();
};

export const invokeNativeAppLogin = () => {
  const delivered = postNativeBridgeMessage("invokeNativeLogin", {});
  windowsAndroidNativeMethods("login");

  return delivered || isAndroidAppWebView();
};

/** Lets signup Back open web /login inside the app WebView without a native handoff. */
export const ALLOW_WEB_LOGIN_KEY = "ep_allow_web_login";

export const allowWebLoginInNativeShell = () => {
  try {
    localStorage.setItem(ALLOW_WEB_LOGIN_KEY, "1");
  } catch {
    // ignore
  }
};

export const clearAllowWebLoginInNativeShell = () => {
  try {
    localStorage.removeItem(ALLOW_WEB_LOGIN_KEY);
  } catch {
    // ignore
  }
};

export const isWebLoginAllowedInNativeShell = () => {
  try {
    if (localStorage.getItem(ALLOW_WEB_LOGIN_KEY) === "1") {
      return true;
    }
  } catch {
    // ignore
  }

  try {
    return (
      new URLSearchParams(window.location.search || "").get("allow_web") === "1"
    );
  } catch {
    return false;
  }
};

/**
 * Signup Back inside the app shell: hand off to the native sign-in screen.
 *
 * `invokeNativeLogin` cannot carry this on iOS — that handler stands down while
 * a token is stored, and mid-signup there always is one. `invokeNativeLogout`
 * does fire, but it also switches off the user's Face ID preference, so backing
 * out of signup gets its own message.
 */
export const abandonSignupToNativeLogin = () => {
  try {
    if (typeof window !== "undefined") {
      window.__EP_NATIVE_SESSION_READY__ = false;
    }
  } catch {
    // ignore
  }

  const delivered = postNativeBridgeMessage("abandonSignup", {});
  // The Android shell has no equivalent message; its handoff is a method call.
  windowsAndroidNativeMethods("login");

  return delivered || isAndroidAppWebView();
};

/**
 * Header Back on signup steps: leave the flow and return to Login.
 *
 * In a browser that means the web Login page. Inside the app shell it means the
 * native sign-in screen — web Login is a dead end there (nothing on it routes
 * back to native) and the native session would otherwise survive in the
 * Keychain and resume this very signup on the next launch.
 */
export const exitSignupToLogin = (navigate) => {
  disableLeavePageWarning();
  clearSignupInProgressFlag();
  localStorage.removeItem("completed");
  localStorage.removeItem("currentStep");
  localStorage.removeItem("isResumeSignup");
  localStorage.removeItem("userSignupDetails");
  sessionStorage.removeItem("userSignupDetails");
  localStorage.removeItem("user_Id");
  localStorage.removeItem("errorCount");
  localStorage.removeItem("skip");
  localStorage.removeItem("promoCode");
  localStorage.removeItem("setLoginUserToken");
  localStorage.removeItem("plain_signup");
  localStorage.removeItem("category");
  localStorage.removeItem("signup_product_tier");
  localStorage.removeItem("authToken");
  localStorage.removeItem("authorised");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("roles");
  localStorage.removeItem("uID");
  localStorage.removeItem("loggedInEmail");

  if (typeof window !== "undefined") {
    window.__EP_NATIVE_SESSION_READY__ = false;
  }

  if (isNativeAppShell() && abandonSignupToNativeLogin()) {
    return true;
  }

  // Only now: the flag suppresses every guard that would pull the user off web
  // Login, so it must not be set while a native handoff is still possible.
  allowWebLoginInNativeShell();

  if (typeof navigate === "function") {
    navigate("/login?allow_web=1", { replace: true });
  } else if (typeof window !== "undefined") {
    window.location.replace("/login?allow_web=1");
  }

  return true;
};

/** The native app sets this before handing an unfinished signup back to the web. */
export const hasSignupResumeMarker = () => {
  try {
    const marker = localStorage.getItem("isResumeSignup");
    return marker === "1" || marker === "true";
  } catch {
    return false;
  }
};

/**
 * A customer mid-signup legitimately lives on /create-account, so that route only
 * counts as a login route once signup is finished. Mirrors isLoginUrl() in the iOS
 * WebView guard — both sides must agree or the app ejects users mid-signup.
 */
export const isWebLoginRoute = (
  pathname = window.location.pathname,
  signupResumeActive = false,
) => {
  const path = (pathname || "").toLowerCase();

  return (
    path === "/login" ||
    path.endsWith("/login") ||
    path.includes("/reset-password") ||
    (!signupResumeActive && path.includes("/create-account")) ||
    path.includes("/servicer-login") ||
    path.includes("investor-login")
  );
};

export const hasActiveWebSession = () => {
  try {
    const token = localStorage.getItem("authToken");
    return Boolean(
      token &&
      token !== "null" &&
      token !== "undefined" &&
      localStorage.getItem("authorised") === "true",
    );
  } catch {
    return false;
  }
};

/** Keep native apps on native login — never leave users on web /login. */
export const guardNativeLoginRoute = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (!(isNativeAppWebView() || isIosApp())) {
    return false;
  }

  // Signup Back could not reach native login, so web Login is the fallback.
  if (isWebLoginAllowedInNativeShell()) {
    return false;
  }

  const signupResumeActive =
    hasSignupResumeMarker() || isIncompleteSignupSession();

  if (!isWebLoginRoute(window.location.pathname, signupResumeActive)) {
    return false;
  }

  if (hasActiveWebSession() || window.__EP_NATIVE_SESSION_READY__) {
    return false;
  }

  return invokeNativeAppLogout();
};

/** Web logout/login redirect; native apps hand off to native screens. */
export const redirectToLogin = (navigate, options = {}) => {
  const { path = "/login", replace = false, ...rest } = options || {};

  // Never leave iOS/Android WebViews on web /login — hand off to native Login.
  // When no shell can be reached (stale `isIos` flag, plain mobile browser) fall
  // through to the web login page rather than dead-ending on the current screen.
  if ((isNativeAppWebView() || isIosApp()) && invokeNativeAppLogout()) {
    return true;
  }

  const carLoginUrl = path === "/login" ? carLoginUrlForServicerExit() : null;
  if (carLoginUrl) {
    window.location.href = carLoginUrl;
    return true;
  }

  if (typeof navigate === "function") {
    navigate(path, { replace, ...rest });
    return true;
  }

  window.location.replace(path);
  return true;
};

/**
 * Company agent/attorney login path for branded broker/title/insurance/prefix routes.
 * Returns null when the path is not a company portal URL.
 */
export const resolveCompanyAgentLoginPath = (path = "") => {
  const pathname = String(path || "");
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0] || "";
  const companySlug = segments[1] || "";

  if (first === "broker" && companySlug) {
    return `/broker/${companySlug}/agent-login`;
  }
  if (first === "title" && companySlug) {
    return `/title/${companySlug}/agent-login`;
  }
  if (first === "insurance" && companySlug) {
    return `/insurance/${companySlug}/agent-login`;
  }
  if (first === "attorney" && companySlug) {
    return `/attorney/${companySlug}/attorney-login`;
  }

  const allowedPrefixObj = findAllowedPrefixData(first);
  if (getObjLength(allowedPrefixObj) && allowedPrefixObj?.source === "title") {
    return `/${allowedPrefixObj.prefix}/agent-login`;
  }

  return null;
};

const redirectToWebLogin = (path = window.location.pathname, session = "") => {
  const carLoginUrl = carLoginUrlForServicerExit(session);
  if (carLoginUrl) {
    window.location.replace(carLoginUrl);
    return;
  }

  if (path?.includes("investor")) {
    window.location.replace("/investor-login");
    return;
  }

  const companyLoginPath = resolveCompanyAgentLoginPath(path);
  if (companyLoginPath) {
    localStorage.removeItem("previousPath");
    localStorage.removeItem("path");
    window.location.replace(companyLoginPath);
    return;
  }

  window.location.replace("/login");
};

export const SESSION_EXPIRED_NOTICE =
  "Your token is expired or invalid. Please Login again.";

/** Clear expired sessions once; native apps return to native login (no web /login). */
export const handleExpiredSessionRedirect = (
  path = window.location.pathname,
  options = {},
) => {
  if (typeof window === "undefined") {
    return false;
  }

  const now = Date.now();
  const lastHandled = window.__epSessionExpiredHandledAt || 0;
  if (now - lastHandled < SESSION_EXPIRED_DEBOUNCE_MS) {
    return false;
  }

  const shouldRedirect = handleUnauthorizedApiResponse(path);
  if (!shouldRedirect) {
    return false;
  }

  window.__epSessionExpiredHandledAt = now;

  const archived = Boolean(options?.accountArchived);
  const notice =
    options?.message ||
    (archived
      ? getAccountArchivedMessage(options?.responseData)
      : SESSION_EXPIRED_NOTICE);

  try {
    if (archived) {
      localStorage.setItem("sessionEndNotice", notice);
    } else {
      localStorage.removeItem("sessionEndNotice");
    }
  } catch {
    // ignore storage failures
  }

  if (isNativeAppWebView() || isIosApp()) {
    const handedOff = invokeNativeAppLogout(
      archived
        ? { reason: "account_archived", message: notice }
        : { reason: "session_expired" },
    );

    if (handedOff) {
      return true;
    }
  }

  toast.error(notice);
  redirectToWebLogin(path, archived ? "archived" : "expired");
  return true;
};

export const redirectToLoginForInvalidSession = (
  message,
  statusCode,
  responseData,
) => {
  if (
    statusCode !== 401 &&
    !isUnknownUserApiMessage(message) &&
    !(
      statusCode === 403 &&
      isAccountArchivedApiResponse(responseData || { message })
    )
  ) {
    return false;
  }

  return handleExpiredSessionRedirect(window.location.pathname, {
    accountArchived:
      statusCode === 403 &&
      isAccountArchivedApiResponse(responseData || { message }),
    message:
      statusCode === 403
        ? getAccountArchivedMessage(responseData || { message })
        : undefined,
    responseData,
  });
};

export const ENHANCED_MONITORING_ALREADY_ACTIVE_MESSAGE =
  "Enhanced Monitoring is already active for one or more selected properties.";

export const isEnhancedMonitoringAlreadyActiveError = (response) => {
  if (!response || response.success) {
    return false;
  }

  const message = response?.data?.message || response?.message || "";

  return (
    response?.statuscode === 409 ||
    message === ENHANCED_MONITORING_ALREADY_ACTIVE_MESSAGE ||
    message.includes("already active for one or more selected properties")
  );
};

export const redirectToCustomerPropertiesForActiveMonitoring = (response) => {
  if (!isEnhancedMonitoringAlreadyActiveError(response)) {
    return false;
  }

  applyStoredSignupLoginToken();
  disableLeavePageWarning();
  localStorage.removeItem("isResumeSignup");
  window.location.replace(CUSTOMER_HOME_PATH);
  return true;
};

//Clear localstorage values.
export const clearLocalStoarge = (term, clearAll = false) => {
  let subUsersList = localStorage.getItem("subUsersList");
  let category = localStorage.getItem("category");
  let property_search_data = localStorage.getItem("property_search_data");
  let county_details = {};
  let is_scrp_hp = localStorage.getItem("scrp_hp");
  // Kept until the next login decides it (see rememberLoginPortal).
  const loginPortal =
    isServiceProviderPortalHost() && isCarPortalLogin()
      ? "car"
      : localStorage.getItem(LOGIN_PORTAL_KEY);
  if (
    term === "check_county" &&
    is_scrp_hp &&
    !is_scrp_hp.includes("undefined")
  ) {
    county_details = {
      is_scrp_hp,
      countyCode: localStorage.getItem("county-code"),
    };
  }
  localStorage.clear();
  localStorage.setItem("preference", true);
  localStorage.setItem("subUsersList", subUsersList);
  if (loginPortal) {
    localStorage.setItem(LOGIN_PORTAL_KEY, loginPortal);
  }
  // upadte couynty if it's already available
  if (getObjLength(county_details) && !clearAll) {
    localStorage.setItem("scrp_hp", county_details?.is_scrp_hp);
    localStorage.setItem("county-code", county_details?.countyCode);
  }
  if (category && !clearAll) {
    localStorage.setItem("category", category);
  }
  if (property_search_data && !clearAll) {
    localStorage.setItem("property_search_data", property_search_data);
  }
};

// Managed the sub user's login counts
export const subUsersLoginCount = (uID) => {
  let data =
    JSON.parse(decryptPayload(localStorage.getItem("subUsersList"))) || [];
  const userExistsIndex = data.findIndex((item) => item.user_id === uID);
  if (userExistsIndex !== -1) {
    // User exists, update count
    data[userExistsIndex].count += 1;
  } else {
    // User does not exist, add new user
    data.push({ user_id: uID, count: 0 });
  }
  localStorage.setItem("subUsersList", encryptPayload(JSON.stringify(data)));
};

// Managed the sub user's login counts
export const richSnippetImplementation = (uID) => {
  let scriptElement = null;
  if (SERVER === "production") {
    scriptElement = document.createElement("script");
    scriptElement.type = "application/ld+json";
    scriptElement.text = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "WebSite",
      name: "Equity Protect",
      url: "https://www.equityprotect.com/",
      potentialAction: {
        "@type": "SearchAction",
        target:
          "https://www.equityprotect.com/usecases{search_term_string}{search_term_string}",
        "query-input": "required name=search_term_string",
      },
    });
  }

  // Return null if not in production
  return scriptElement;
};

// Focus the first OTP / token digit input on verification screens.
export const autoFocusOnField = (container) => {
  const root =
    typeof container === "string"
      ? document.querySelector(container)
      : container instanceof Element
        ? container
        : document;

  if (!root) {
    return;
  }

  const query = (selector) =>
    root === document
      ? document.querySelector(selector)
      : root.querySelector(selector);

  const focusInput = (input) => {
    if (!input || input.disabled || typeof input.focus !== "function") {
      return false;
    }

    input.focus();
    if (typeof input.setSelectionRange === "function") {
      try {
        input.setSelectionRange(0, 0);
      } catch {
        // Some input types do not support selection ranges.
      }
    }

    return true;
  };

  setTimeout(() => {
    const selectors = [
      '[data-otp-slot="0"]',
      '[aria-label="Please enter verification code. Digit 1"]',
      ".otpBoxWrapper input:not([disabled])",
      ".otp input:not([disabled])",
      'input[inputmode="numeric"][maxlength="1"]:not([disabled])',
      "[data-otp-input]:not([disabled])",
    ];

    for (const selector of selectors) {
      const candidate = query(selector);
      if (focusInput(candidate)) {
        return;
      }
    }

    const otpWrapper = query(".otpBoxWrapper");
    if (otpWrapper) {
      const firstWrapperInput = otpWrapper.querySelector(
        "input:not([disabled])",
      );
      if (focusInput(firstWrapperInput)) {
        return;
      }
    }

    const genericFirst = query(
      'form[id*="token"] input:not([disabled]), form[id*="otp"] input:not([disabled]), form[id*="verify"] input:not([disabled])',
    );
    focusInput(genericFirst);
  }, 100);
};

// to set OTP foucs on the 1st input field
export const convertPriceStringToNumber = (priceString) => {
  // Remove commas from the string
  const stringWithoutCommas = priceString.replace(/,/g, "");
  // Parse the string into a float number
  const numberValue = parseFloat(stringWithoutCommas);
  // If the parsed number has decimal places, return it as is, otherwise, return the integer part
  return isNaN(numberValue)
    ? null
    : Number.isInteger(numberValue)
      ? parseInt(numberValue)
      : numberValue;
};

// fecth the custom domain name
export const customeDomainName = (url) => {
  console.log("pathname==>", url);
  const getURL = url?.split(".");
  let customeDomainName = null;
  if (getURL?.[0]?.includes(SUBDOMAIN?.equityprotect)) {
    customeDomainName = getURL?.[1];
  } else {
    customeDomainName = null;
  }

  return customeDomainName;
};

// calculate how many days behind a specific date
export const calculateDaysBehind = (inputDateString) => {
  const inputDate = new Date(inputDateString);
  const currentDate = new Date();

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = currentDate - inputDate;

  // Convert milliseconds to days
  const differenceInDays = Math.floor(
    differenceInMilliseconds / (1000 * 60 * 60 * 24),
  );

  return differenceInDays;
};

// sepearte data objects for property risk report payload
export const propertyRiskReportDataObj = (data) => {
  console.log("propertyRiskReportDataObj, data==>", data);
  const rawName = String(data?.name || "").trim();
  const nameParts = rawName.split(/\s+/).filter(Boolean);
  const nameFromReport = looksLikeStreetAddress(rawName)
    ? { first: "", last: "" }
    : {
        first: nameParts[0] || "",
        last: nameParts.slice(1).join(" ") || "",
      };
  let user_data = {
    first_name:
      sanitizeBillingNameValue(data?.first_owner_first_name) ||
      nameFromReport.first,
    last_name:
      sanitizeBillingNameValue(data?.first_owner_last_name) ||
      nameFromReport.last,
    first_owner_first_name:
      sanitizeBillingNameValue(data?.first_owner_first_name) ||
      nameFromReport.first,
    first_owner_last_name:
      sanitizeBillingNameValue(data?.first_owner_last_name) ||
      nameFromReport.last,
    email: data?.email,
    phone: data?.phone,
    hh_uid: data?.hh_uid,
    category: data?.category || undefined,
    property_id: data?.property_id || undefined,
    risk_report_id: data?.risk_report_id || undefined,
  };
  let param = {
    street_number: data?.street_number,
    site_route: data?.site_route,
    city: data?.city,
    state: data?.state,
    state_name: data?.state_name,
    zip: data?.zip,
    unit_no: data?.unit_no,
    county: data?.county,
  };
  let fixParams = {
    country: data?.country,
    latitude: data?.latitude,
    longitude: data?.longitude,
    address: data?.address_1 || data?.address,
  };
  let searched_Add = data?.searched_Add || data?.address;
  let property_search_data = { user_data, param, fixParams, searched_Add };
  return property_search_data;
};

// To count the risk level
export const riskLevelCount = (status) => {
  let count = 0;
  if (status === "Low") {
    count = 1;
  } else if (status === "Medium-Low") {
    count = 2;
  } else if (status === "Medium") {
    count = 3;
  } else if (status === "Medium-High") {
    count = 4;
  } else if (status === "High") {
    count = 5;
  }
  return count;
};

//Calling Andriod app methods on login/biometric/logout
export const windowsAndroidNativeMethods = (type, payload) => {
  console.log(type, "Android method called...", window?.Android);
  let androidMethod = null;
  console.log(
    window?.Android,
    "<== is android -- Android method type==>",
    type,
  );
  if (window?.Android) {
    if (type === "login") {
      console.log("Android Login method called...");
      androidMethod = window?.Android?.login();
    } else if (type === "biometric") {
      console.log(
        "Android Biometric method called...",
        JSON.stringify(payload),
      );
      androidMethod = window?.Android?.biometric(JSON.stringify(payload));
    } else if (type === "logout") {
      console.log("Android Logout method called...");
      androidMethod = window?.Android?.logout();
    } else {
      androidMethod = null;
    }
  }
  return androidMethod;
};

/**
 * Returns true when the app is rendered inside a native WebView container.
 *
 * We intentionally do NOT rely on localStorage flags here because they can be set
 * on normal mobile browsers.
 */
export function isAndroidAppWebView() {
  if (typeof window === "undefined") return false;
  return !!window?.Android;
}

export function isNativeAppWebView() {
  if (typeof window === "undefined") return false;

  // Android bridge typically exposed as window.Android by the WebView wrapper.
  if (isAndroidAppWebView()) return true;

  // iOS WebKit bridge typically exposes window.webkit.messageHandlers.
  // eslint-disable-next-line no-undef
  return !!window?.webkit?.messageHandlers;
}

/** True when modal touch fixes should apply (native iOS/Android WebView). */
export function isNativeAppDialog() {
  return isNativeAppWebView() || isIosApp();
}

/** True inside the EquityProtect iOS/Android app shell (not a mobile browser). */
export function isNativeAppShell() {
  return isIosApp() || isAndroidAppWebView() || isNativeAppWebView();
}

/** Marketing pages that must never render inside the native app WebView. */
export function isNativeBlockedMarketingPath(
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
) {
  const path = (pathname || "/").replace(/\/+$/, "") || "/";
  return path === "/" || path === "/equity-protect";
}

/**
 * Where a native WebView should go instead of the public homepage.
 * Mid-signup resumes create-account; authenticated customers go to Properties;
 * otherwise hand off to the native login flow via the web login route.
 */
export function getNativeAppSafePath() {
  if (isIncompleteSignupSession()) {
    return getSignupResumePath();
  }
  if (isAuthenticatedSession()) {
    return CUSTOMER_HOME_PATH;
  }
  return "/login";
}

export const findCountyLocation = (pathname) => {
  const safePath = (pathname || "").toString().toLowerCase();
  const fecthCountyData = CountyVertical?.find(
    (data) => (data?.county_code || "").toLowerCase() === safePath,
  );
  return getObjLength(fecthCountyData);
};

// Returns { isAttorney } | { isIndustryPartner } | { isCounty, county } | null
export const findAllowedAttorneyPrefix = (prefix) => {
  const safe = (prefix || "").toString().toLowerCase();
  if (safe === "attorney-title-program") return { isAttorney: true };
  if (safe === "industrypartner") return { isIndustryPartner: true };
  const county = CountyVertical?.find(
    (c) =>
      (c?.county_code || "").toLowerCase() === safe ||
      (c?.county_code1 || "").toLowerCase() === safe,
  );
  if (county) return { isCounty: true, county };
  return null;
};

export const findCountyLocationData = (pathname, type = "county_code") => {
  const key = type || "county_code";
  const safePath = (pathname || "").toString().toLowerCase();
  const fecthCountyData = CountyVertical?.find(
    (data) => (data?.[key] || "").toString().toLowerCase() === safePath,
  );

  return fecthCountyData;
};
/** One-time setup-fee credit (in dollars) a county's residents are shown. */
export const getCountySetupFeeCredit = (county) => county?.setupFeeCredit ?? 85;

/**
 * County name and portal code for county investor activity logs, e.g.
 * { county_name: "Benton", county_portal: "bentoncountyin" }. Empty off county pages.
 */
export const getCountyLogPayload = (countyCode) => {
  const county = countyCode ? findCountyLocationData(countyCode) : null;
  return county
    ? { county_name: county.county_name, county_portal: county.county_code }
    : {};
};

export const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getCategoryDisplayLabel = (category) => {
  switch (category) {
    case "complete_protection":
      return "Complete Protection";
    case "basic_monitoring":
      return "Basic Monitoring";
    case "enhanced_monitoring":
      return "Enhanced Alerts";
    default:
      return null;
  }
};

export const getSubscriptionPlanDisplayLabel = (property) => {
  const subscription = property?.subscription;
  if (!subscription) {
    return "-";
  }

  const rawPlanName =
    subscription?.plan?.name ||
    subscription?.plan_name ||
    property?.subscription_plan_name;

  if (!rawPlanName) {
    return "-";
  }

  const normalized = String(rawPlanName).trim().toLowerCase();
  if (normalized === "yearly" || normalized === "annual") {
    return "Annual";
  }
  if (normalized === "monthly") {
    return "Monthly";
  }

  return capitalizeFirstLetter(rawPlanName);
};

/** API id for subscription-history / property-detail on monitoring-tier properties. */
export const resolvePropertyHistoryId = (property) => {
  if (!property) return null;
  const category = property?.category;
  if (
    (category === "enhanced_monitoring" || category === "basic_monitoring") &&
    property?.risk_report_id
  ) {
    return property.risk_report_id;
  }
  return property?.id ?? null;
};

export const resolvePropertyDetailId = (property) =>
  resolvePropertyHistoryId(property);

const MONITORING_SIGNUP_CATEGORIES = new Set([
  "enhanced_monitoring",
  "basic_monitoring",
]);

export const isMonitoringProductTier = (tier) =>
  MONITORING_SIGNUP_CATEGORIES.has(tier);

export const resolveStoredProductTier = () =>
  decryptPayload(localStorage.getItem("category")) ||
  localStorage.getItem("signup_product_tier") ||
  null;

export const resolveCustomerProductTier = ({ userProfileDetails } = {}) =>
  userProfileDetails?.data?.category ??
  userProfileDetails?.data?.product_tier ??
  resolveStoredProductTier();

/** Postcards are only for Complete Protection customers who manually add properties. */
export const shouldPromptPostcardVerification = (options = {}) => {
  const tier = options.tier ?? resolveCustomerProductTier(options);
  return !isMonitoringProductTier(tier);
};

// Other login popups wait on this flag; the event tells them it is gone.
export const STANUP_POPUP_CLEARED_EVENT = "stanup-popup-cleared";

export const clearStanupPopupPending = () => {
  localStorage.removeItem("stanupPopupCount");
  window.dispatchEvent(new Event(STANUP_POPUP_CLEARED_EVENT));
};

export const setStanupPopupCountIfEligible = (tier, options = {}) => {
  if (shouldPromptPostcardVerification({ ...options, tier })) {
    localStorage.setItem("stanupPopupCount", "1");
  } else {
    localStorage.removeItem("stanupPopupCount");
  }
};

export const normalizeSubscriptionCategory = (item) =>
  item?.category ?? item?.product_tier ?? item?.product_tire ?? null;

export const deriveProfileCategoryFromProperties = (propertiesData = []) => {
  if (!Array.isArray(propertiesData) || propertiesData.length === 0) {
    return null;
  }
  const tiers = propertiesData
    .map((property) => normalizeSubscriptionCategory(property))
    .filter(Boolean);
  if (tiers.some((tier) => tier === "complete_protection")) {
    return "complete_protection";
  }
  if (tiers.some((tier) => tier === "enhanced_monitoring")) {
    return "enhanced_monitoring";
  }
  if (tiers.every((tier) => tier === "basic_monitoring")) {
    return "basic_monitoring";
  }
  return null;
};

/** Prefer Complete Protection from loaded properties over a stale monitoring profile tier. */
export const resolveCustomerProfileCategory = ({
  profileCategory = null,
  profileProductTier = null,
  propertiesData = [],
} = {}) => {
  const fromProperties = deriveProfileCategoryFromProperties(propertiesData);
  if (fromProperties === "complete_protection") {
    return "complete_protection";
  }

  const fromProfile = profileCategory ?? profileProductTier ?? null;
  if (fromProfile === "complete_protection") {
    return "complete_protection";
  }

  return fromProperties ?? fromProfile;
};

/**
 * True when the customer has only Basic Monitoring subscriptions (no Enhanced or Complete Protection).
 * Basic/free monitoring users have no paid property-subscription rows; treat empty subscriptions
 * as basic-only unless the profile is explicitly enhanced or complete protection.
 */
export const isBasicPlanOnlyCustomer = ({
  subscriptions = [],
  profileCategory = null,
  subscriptionsLoaded = false,
} = {}) => {
  const isPaidTierProfile =
    profileCategory === "enhanced_monitoring" ||
    profileCategory === "complete_protection";

  if (profileCategory === "basic_monitoring") {
    return true;
  }

  if (subscriptionsLoaded) {
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
      return !isPaidTierProfile;
    }
    return subscriptions.every(
      (item) => normalizeSubscriptionCategory(item) === "basic_monitoring",
    );
  }

  if (isPaidTierProfile) {
    return false;
  }

  // Tier unknown until profile/subscriptions load — default to hiding for free monitoring users.
  return true;
};

/** True when the customer is on Basic or Enhanced monitoring (not Complete Protection). */
export const isMonitoringTierCustomer = ({
  subscriptions = [],
  profileCategory = null,
  subscriptionsLoaded = false,
} = {}) => {
  if (
    profileCategory === "basic_monitoring" ||
    profileCategory === "enhanced_monitoring"
  ) {
    return true;
  }

  if (profileCategory === "complete_protection") {
    return false;
  }

  if (subscriptionsLoaded) {
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
      return profileCategory !== "complete_protection";
    }
    return subscriptions.every((item) => {
      const category = normalizeSubscriptionCategory(item);
      return (
        category === "basic_monitoring" || category === "enhanced_monitoring"
      );
    });
  }

  return false;
};

const ELEVATED_RISK_LEVELS = new Set(["MEDIUM", "HIGH", "CRITICAL"]);

const riskLevelKeyFromScore = (score) => {
  if (!Number.isFinite(score)) return null;
  if (score <= 2) return "LOW";
  if (score === 3) return "MEDIUM";
  return "HIGH";
};

export const normalizePropertyRiskLevelKey = (property) => {
  const score = Number(property?.risk_score);
  const fromScore = riskLevelKeyFromScore(score);
  if (fromScore) return fromScore;

  const raw = property?.risk_level || property?.risk_status || "";
  const normalized = String(raw).trim().toLowerCase();
  if (normalized.includes("critical")) return "CRITICAL";
  if (normalized.includes("high")) return "HIGH";
  if (normalized.includes("medium") || normalized.includes("moderate")) {
    return "MEDIUM";
  }
  if (normalized.includes("low")) return "LOW";
  return "UNKNOWN";
};

export const isElevatedPropertyRisk = (property) => {
  const score = Number(property?.risk_score);
  if (Number.isFinite(score)) {
    return score >= 3;
  }
  return ELEVATED_RISK_LEVELS.has(normalizePropertyRiskLevelKey(property));
};

/** Monitoring-tier properties that have not been upgraded to Complete Protection. */
export const isUnlockedMonitoringProperty = (property) => {
  const category = normalizeSubscriptionCategory(property);
  if (category === "complete_protection") {
    return false;
  }
  if (
    category === "basic_monitoring" ||
    category === "enhanced_monitoring" ||
    property?.from_risk_measure
  ) {
    return true;
  }
  return false;
};

export const propertyNeedsProtectionUpgrade = (property) =>
  isUnlockedMonitoringProperty(property) && isElevatedPropertyRisk(property);

export const findElevatedRiskUnlockedProperties = (properties = []) =>
  (Array.isArray(properties) ? properties : []).filter(
    propertyNeedsProtectionUpgrade,
  );

/** Parses signup URL search string (handles `&&` in legacy links). */
export const parseSignupSearchParams = (search = "") => {
  const normalized = (search || "").replace(/&&/g, "&");
  const query = normalized.startsWith("?") ? normalized.slice(1) : normalized;
  return new URLSearchParams(query);
};

/**
 * Parses TTB document preview redirect URLs, e.g.
 * `/?ttb_doc_preview&&token=<fetch_key>`
 */
export const parseTtbDocPreviewParams = (search = "") => {
  const params = parseSignupSearchParams(search);
  if (!params.has("ttb_doc_preview")) return null;

  const fetchKey = params.get("fetch_key");
  if (!fetchKey) return null;

  return { fetchKey };
};

export const isMonitoringSignupCategory = (category, planType) =>
  MONITORING_SIGNUP_CATEGORIES.has(category) ||
  MONITORING_SIGNUP_CATEGORIES.has(planType || "");

/**
 * True when user entered signup from a property risk report flow.
 * Supports:
 * - /create-account?report_token=...&plan_type=enhanced_monitoring
 * - /create-account?from=risk_report
 */
export const resolveIsFromRiskReport = ({
  search = "",
  category = "",
} = {}) => {
  const params = parseSignupSearchParams(search);
  const planType = params.get("plan_type");

  if (!isMonitoringSignupCategory(category, planType)) {
    return false;
  }

  return (
    Boolean(params.get("report_token")) || params.get("from") === "risk_report"
  );
};

// Filter out the prefix from ALLOWED_PREFIXES and allow to enter if found, otherwise redirect
export const findAllowedPrefixData = (prefixVal) => {
  return ALLOWED_PREFIXES.find(
    (item) => item.prefix === prefixVal?.toLowerCase(),
  );
};
